import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Button,
  Modal,
  Form,
  Table,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Dropdown,
  Badge,
  Alert,
  Spinner,
  Accordion,
  FloatingLabel,
  Nav,
  ButtonGroup,
  DropdownButton
} from "react-bootstrap";
import { Plus, Forward, Trash2, CalendarCheck, Printer, Mail, SquarePen, CircleOff, Video, MapPin, UsersRound, ClipboardCheck, QrCode } from 'lucide-react';
import Navbar from "../Navbar";
import axios from "axios";
import "bootstrap-icons/font/bootstrap-icons.css";
import { API_ENDPOINTS, API_BASE_URL } from '../config/api';
import { useDelegations } from "../contexts/DelegationsContext";
import ConferenceSchedulingModal from "../components/ConferenceSchedulingModal";
import ConferenceDetailsModal from "../components/ConferenceDetailsModal";
import AttendanceTracker from "../components/AttendanceTracker";
import ConferenceDetailsForm from "../components/ConferenceDetailsForm";
import QRCodeDisplay from "../components/QRCodeDisplay";
import AttendanceSummaryModal from "../components/AttendanceSummaryModal";
import PageTitle from '../components/PageTitle';
import {
  buildPrintDocument,
  openPrintWindow
} from '../utils/printUtility';
import {
  DEFAULT_CONFERENCE_TYPE_OPTIONS,
  resolveConferenceTypeDisplay,
  toTransparentBadgeStyle
} from '../constants/conferenceTypes';

// File icon helper (aligned with onboarding page)
const getFileIcon = (fileName, mimeType) => {
  const extension = fileName ? fileName.split('.').pop().toLowerCase() : '';
  const type = mimeType ? mimeType.toLowerCase() : '';

  // Image files
  if (
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension) ||
    type.includes('image/')
  ) {
    return 'bi bi-image-fill text-info';
  }

  // PDF files
  if (extension === 'pdf' || type.includes('pdf')) {
    return 'bi bi-file-earmark-pdf-fill text-danger';
  }

  // Word documents
  if (
    ['doc', 'docx'].includes(extension) ||
    type.includes('word') ||
    type.includes('document')
  ) {
    return 'bi bi-file-earmark-word-fill text-primary';
  }

  // Excel files
  if (
    ['xls', 'xlsx'].includes(extension) ||
    type.includes('excel') ||
    type.includes('spreadsheet')
  ) {
    return 'bi bi-file-earmark-excel-fill text-success';
  }

  // PowerPoint files
  if (
    ['ppt', 'pptx'].includes(extension) ||
    type.includes('powerpoint') ||
    type.includes('presentation')
  ) {
    return 'bi bi-file-earmark-ppt-fill text-warning';
  }

  // Text files
  if (['txt', 'rtf', 'md'].includes(extension) || type.includes('text/')) {
    return 'bi bi-file-earmark-text-fill text-secondary';
  }

  // Default document icon
  return 'bi bi-file-earmark-text-fill text-warning';
};

// Document type helper for grouping/filtering
const getDocumentTypeLabel = (fileName, mimeType) => {
  const lower = (fileName || '').toLowerCase();
  const mime = (mimeType || '').toLowerCase();

  if (mime.includes('pdf') || lower.endsWith('.pdf')) return 'PDF';
  if (mime.includes('presentation') || mime.includes('powerpoint') || lower.match(/\.(ppt|pptx)$/)) return 'Slides';
  if (mime.includes('spreadsheet') || mime.includes('excel') || lower.match(/\.(xls|xlsx|csv)$/)) return 'Spreadsheet';
  if (mime.includes('word') || mime.includes('document') || lower.match(/\.(doc|docx)$/)) return 'Document';
  if (mime.includes('image') || lower.match(/\.(png|jpg|jpeg|gif|svg|webp)$/)) return 'Image';
  if (mime.includes('video') || lower.match(/\.(mp4|mov|avi|mkv|webm)$/)) return 'Video';
  if (mime.includes('audio') || lower.match(/\.(mp3|wav|aac|flac)$/)) return 'Audio';

  return 'Other';
};

// Helper function to get current academic year
const getCurrentAcademicYear = () => {
  const now = new Date();
  const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    year,
    start: `${year}-07-01`,
    end: `${year + 1}-06-30`,
    display: `${year}-${year + 1}`
  };
};

// Helper function to get current month in YYYY-MM format
const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

// Helper function to get academic year for a specific year
const getAcademicYearForYear = (year) => {
  return {
    year: parseInt(year),
    start: `${year}-07-01`,
    end: `${parseInt(year) + 1}-06-30`,
    display: `${year}-${parseInt(year) + 1}`
  };
};

// Helper function to get months between two dates
const getMonthsBetween = (start, end) => {
  if (!start || !end) return [];

  const startDate = new Date(start + "T00:00:00");
  const endDate = new Date(end + "T00:00:00");

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return [];
  }

  const months = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const monthStr = `${year}-${month}`;

    months.push(monthStr);
    currentDate = new Date(year, currentDate.getMonth() + 1, 1);
  }

  return months;
};

// Helper function to get all dates in the current month
const getDatesInMonth = (yearMonth) => {
  const [year, month] = yearMonth.split("-");
  const date = new Date(year, month - 1, 1);
  const dates = [];
  while (date.getMonth() === month - 1) {
    dates.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return dates;
};

// Helper function to format time from 24-hour to 12-hour with AM/PM
const formatTime = (timeString) => {
  if (!timeString) return '';
  
  try {
    // Parse the time string (format: "08:00:00")
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const minute = parseInt(minutes);
    
    // Convert to 12-hour format
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    
    // Format with leading zeros for minutes
    const displayMinute = minute.toString().padStart(2, '0');
    
    return `${displayHour}:${displayMinute} ${period}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return timeString; // Return original if parsing fails
  }
};

// Helper function to get conferences for a specific date
const getConferencesForDate = (date, conferences) => {
  if (!date) return [];
  const dateStr = date.toISOString().split('T')[0];
  const conferencesForDate = [];
  if (!Array.isArray(conferences)) {
    return conferencesForDate;
  }
  conferences.forEach(conference => {
    conference.sessions?.forEach(session => {
      if (session.date === dateStr) {
        conferencesForDate.push({
          ...conference,
          session
        });
      }
    });
  });

  // Sort sessions by start time (fall back to talkTime, then title)
  const parseTime = (t) => {
    if (!t) return Number.MAX_SAFE_INTEGER;
    // Expect "HH:MM:SS" or "HH:MM"
    const parts = t.split(':').map(Number);
    if (parts.length < 2 || parts.some(isNaN)) return Number.MAX_SAFE_INTEGER;
    const [h, m] = parts;
    return h * 60 + m;
  };

  return conferencesForDate.sort((a, b) => {
    const aTime = parseTime(a.startTime || a.session?.talkTime);
    const bTime = parseTime(b.startTime || b.session?.talkTime);
    if (aTime !== bTime) return aTime - bTime;
    return (a.title || '').localeCompare(b.title || '');
  });
};

// Helper function to check if Record Attendance button should be disabled
const shouldDisableRecordAttendance = (sessionDate, endTime) => {
  if (!sessionDate || !endTime) return false;
  
  try {
    // Create session end datetime
    const sessionEndDateTime = new Date(`${sessionDate}T${endTime}`);
    const now = new Date();
    
    // Calculate one hour after session ends
    const oneHourAfterEnd = new Date(sessionEndDateTime.getTime() + (60 * 60 * 1000));
    
    // Disable button if current time is more than 1 hour after session ends
    return now > oneHourAfterEnd;
  } catch (error) {
    console.error('Error checking record attendance button state:', error);
    return false;
  }
};

// Helper function to check if attendance code should be shown (1 hour before start)
const shouldShowAttendanceCode = (sessionDate, startTime) => {
  if (!sessionDate || !startTime) return false;
  
  try {
    const eventDateTime = new Date(`${sessionDate}T${startTime}`);
    const now = new Date();
    const oneHourBefore = new Date(eventDateTime.getTime() - (60 * 60 * 1000));
    return now >= oneHourBefore;
  } catch (error) {
    console.error('Error checking attendance code visibility:', error);
    return false;
  }
};

// Helper function to find the scheduled date closest to today (prefer future, fallback to closest)
const getClosestScheduledDate = (scheduledDates) => {
  if (!scheduledDates || scheduledDates.length === 0) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0); // ignore time
  // Filter for future or today
  const futureOrToday = scheduledDates.filter(date => date >= now);
  if (futureOrToday.length > 0) {
    // Return the soonest future or today
    return futureOrToday.reduce((a, b) => (a < b ? a : b));
  } else {
    // All are in the past, return the most recent past
    return scheduledDates.reduce((a, b) => (a > b ? a : b));
  }
};

/** Mirrors server GET /conferences/month/:yearMonth (overlap + session date range + isActive). */
const buildConferencesForMonthFromList = (conferences, yearMonth, academicYear) => {
  if (!yearMonth || !Array.isArray(conferences)) return [];

  const parts = yearMonth.split("-");
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(y) || isNaN(m) || m < 1 || m > 12) return [];

  const startStr = `${y}-${String(m).padStart(2, "0")}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const endStr = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

  const yearNum = Number(academicYear);

  return conferences
    .filter((conf) => Number(conf.academicYear) === yearNum)
    .filter((conf) => {
      if (conf.isActive === false) return false;
      if (!conf.startDate || !conf.endDate) return false;
      return conf.startDate <= endStr && conf.endDate >= startStr;
    })
    .map((conf) => ({
      ...conf,
      sessions: (conf.sessions || []).filter(
        (s) => s.date && s.date >= startStr && s.date <= endStr
      ),
    }));
};

const filterConferencesByAcademicYear = (conferences, year) => {
  const yearNum = Number(year);
  return (conferences || []).filter((conf) => Number(conf.academicYear) === yearNum);
};

const isMonthInAcademicYear = (yearMonth, academicYear) => {
  const year = Number(academicYear);
  const [y, m] = yearMonth.split("-").map(Number);
  return (y === year && m >= 7) || (y === year + 1 && m <= 6);
};

const buildAvailableAcademicYears = (conferences) => {
  const years = new Set();
  const currentYear = getCurrentAcademicYear().year;
  years.add(currentYear);
  for (let i = currentYear - 3; i <= currentYear + 1; i++) {
    years.add(i);
  }
  if (Array.isArray(conferences)) {
    conferences.forEach((conf) => {
      if (conf.academicYear != null) years.add(Number(conf.academicYear));
    });
  }
  return Array.from(years).sort((a, b) => a - b);
};

const pickDefaultMonth = (availableMonths, academicYear, preferCurrentMonth) => {
  if (!availableMonths.length) {
    return { month: "", index: 0 };
  }

  const monthsInYear = availableMonths.filter((m) => isMonthInAcademicYear(m, academicYear));
  const monthsToUse = monthsInYear.length ? monthsInYear : availableMonths;

  if (preferCurrentMonth) {
    const currentMonthStr = getCurrentMonth();
    if (isMonthInAcademicYear(currentMonthStr, academicYear)) {
      const idx = availableMonths.indexOf(currentMonthStr);
      if (idx !== -1) {
        return { month: currentMonthStr, index: idx };
      }
    }
  }

  const firstMonth = monthsToUse[0];
  return { month: firstMonth, index: availableMonths.indexOf(firstMonth) };
};

const ConferenceSchedulerPage = () => {
  const role = localStorage.getItem('role');
  const { hasModuleAccess } = useDelegations();
  const isDirector = ['director', 'admin', 'attending'].includes(role) || hasModuleAccess('conferenceScheduler');
  const isResident = role === 'resident';

  const [conferencesByMonth, setConferencesByMonth] = useState({}); // { [month]: [conferences] }
  const [loadedMonths, setLoadedMonths] = useState([]); // track which months are loaded
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAttendanceTracker, setShowAttendanceTracker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(getCurrentAcademicYear().year);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [currentMonth, setCurrentMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [exportError, setExportError] = useState("");
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [accordionOpenKeys, setAccordionOpenKeys] = useState({});
  const [showAllDates, setShowAllDates] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(""); // "current", "all", or "series"
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedConference, setSelectedConference] = useState(null);
  const [attendanceData, setAttendanceData] = useState({});
  const [currentUserAttendance, setCurrentUserAttendance] = useState({});
  const [userData, setUserData] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [showDateNavigation, setShowDateNavigation] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState({});
  const [showAttendanceSummaryModal, setShowAttendanceSummaryModal] = useState(false);
  const [selectedSessionForSummary, setSelectedSessionForSummary] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedPGY, setSelectedPGY] = useState("");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [currentYearConferences, setCurrentYearConferences] = useState([]);
  const [allConferences, setAllConferences] = useState([]);
  const [conferenceTypeOptions, setConferenceTypeOptions] = useState(DEFAULT_CONFERENCE_TYPE_OPTIONS);

  // Secondary top-nav tab state
  const [activeSecondaryTab, setActiveSecondaryTab] = useState('conferences'); // 'conferences' | 'documents'

  // Conference documents view state
  const [documentsSearch, setDocumentsSearch] = useState('');
  const [selectedDocumentType, setSelectedDocumentType] = useState('all');
  const [selectedDocumentDateRange, setSelectedDocumentDateRange] = useState('all'); // all, 1w, 2w, 1m, 3m, 6m, 1y
  const [selectedConferenceType, setSelectedConferenceType] = useState('all');

  // Fetch user information for all required attendees
  const fetchConferenceTypePreferences = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.CONFERENCES}/type-preferences`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (Array.isArray(response.data?.types) && response.data.types.length > 0) {
        setConferenceTypeOptions(response.data.types);
      }
    } catch (error) {
      console.error("Error fetching conference type preferences:", error);
    }
  };

  useEffect(() => {
    fetchConferenceTypePreferences();
  }, []);

  // Fetch user information for all required attendees
  const fetchUserData = async (userIds) => {
    if (!userIds || userIds.length === 0) return;
    
    try {
      const uniqueUserIds = [...new Set(userIds)];
      const userPromises = uniqueUserIds.map(userId =>
        axios.get(`${API_ENDPOINTS.USERS}/${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }).catch(error => {
          console.error(`Error fetching user ${userId}:`, error);
          return { data: null };
        })
      );
      
      const userResponses = await Promise.all(userPromises);
      const newUserData = {};
      userResponses.forEach((response, index) => {
        if (response.data) {
          newUserData[uniqueUserIds[index]] = response.data;
        }
      });
      
      setUserData(prev => ({
        ...prev,
        ...newUserData
      }));
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  // Fetch attendance data for a session
  const fetchAttendanceData = async (sessionId) => {
    if (!sessionId) return;
    
    try {
      const response = await axios.get(
        `${API_ENDPOINTS.CONFERENCE_ATTENDANCE}/session/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setAttendanceData(prev => ({
        ...prev,
        [sessionId]: response.data
      }));
      
      // Check if current user has already recorded attendance
      const currentUserId = JSON.parse(localStorage.getItem('user'))?.id;
      const userAttendance = (response.data.attendance || []).find(
        record => record.user.id === currentUserId
      );
      
      setCurrentUserAttendance(prev => ({
        ...prev,
        [sessionId]: userAttendance
      }));
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    }
  };

  useEffect(() => {
    // Find all requiredAttendees for the selected conference(s) and fetch user data if missing
    const monthConfs = conferencesByMonth[currentMonth] || [];
    const activeDate = selectedDate || selected;
    const selectedConferences = getConferencesForDate(activeDate, monthConfs);
    let allRequiredAttendees = [];
    selectedConferences.forEach(conf => {
      if (conf.requiredAttendees) {
        allRequiredAttendees = allRequiredAttendees.concat(conf.requiredAttendees);
      }
    });
    // Only fetch users not already in userData
    const missingUserIds = allRequiredAttendees.filter(id => !userData[id]);
    if (missingUserIds.length > 0) {
      fetchUserData(missingUserIds);
    }
    // eslint-disable-next-line
  }, [attendanceData, selected, selectedDate, conferencesByMonth, currentMonth]);

  useEffect(() => {
    // Fetch attendance data for all sessions on the selected date
    const monthConfs = conferencesByMonth[currentMonth] || [];
    const activeDate = selectedDate || selected;
    const selectedConferences = getConferencesForDate(activeDate, monthConfs);
    const sessionIds = selectedConferences.map(conf => conf.session?.id).filter(Boolean);
    sessionIds.forEach(sessionId => {
      // Always fetch to ensure freshness
      fetchAttendanceData(sessionId);
    });
    // eslint-disable-next-line
  }, [selected, selectedDate, currentMonth, conferencesByMonth]);

  // Check if screen is mobile/small
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Filter conferences by selected academic year
  const getConferencesForYear = (conferences, year) => {
    return filterConferencesByAcademicYear(conferences, year);
  };

  // Get all available months from conferences
  const getAllMonths = (conferences) => {
    const allMonths = new Set();
    
    // Ensure conferences is an array
    if (!Array.isArray(conferences)) {
      console.warn('conferences is not an array:', conferences);
      return [];
    }
    
    conferences.forEach((conference) => {
      if (conference.startDate && conference.endDate) {
        const months = getMonthsBetween(conference.startDate, conference.endDate);
        months.forEach(month => allMonths.add(month));
      }
    });
    return Array.from(allMonths).sort();
  };

  // Same rules as GET /conferences/month/:yearMonth: active conferences overlapping the month,
  // sessions limited to that month (avoids a second HTTP round-trip after the full list load).
  const applyMonthSlice = (yearMonth, academicYear, conferenceList, options = {}) => {
    const { replaceAllMonths = false } = options;
    const monthData = buildConferencesForMonthFromList(conferenceList, yearMonth, academicYear);
    if (replaceAllMonths) {
      setConferencesByMonth({ [yearMonth]: monthData });
      setLoadedMonths([yearMonth]);
    } else {
      setConferencesByMonth((prev) => ({ ...prev, [yearMonth]: monthData }));
      setLoadedMonths((prev) => (prev.includes(yearMonth) ? prev : [...prev, yearMonth]));
    }
    return monthData;
  };

  // Replace fetchAllConferences with a function to fetch months list and first month
  const fetchInitialConferences = async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Please log in to view conferences");
        return;
      }

      // Get current user role
      const userRole = localStorage.getItem('role');
      const shouldShowCurrentMonth =
        ['director', 'resident', 'attending'].includes(userRole) || hasModuleAccess('conferenceScheduler');

      // Fetch all conferences; filter client-side by academic year
      const allResponse = await axios.get(`${API_ENDPOINTS.CONFERENCES}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allConfs = allResponse.data || [];
      setAllConferences(allConfs);
      setAvailableAcademicYears(buildAvailableAcademicYears(allConfs));

      const yearConfs = filterConferencesByAcademicYear(allConfs, selectedAcademicYear);
      setCurrentYearConferences(yearConfs);

      const allMonths = getAllMonths(yearConfs);
      setAvailableMonths(allMonths);

      if (allMonths.length === 0) {
        setCurrentMonth("");
        setCurrentMonthIndex(0);
        setConferencesByMonth({});
        setLoadedMonths([]);
        return;
      }

      const { month: monthToShow, index: monthIndex } = pickDefaultMonth(
        allMonths,
        selectedAcademicYear,
        shouldShowCurrentMonth
      );

      setCurrentMonth(monthToShow);
      setCurrentMonthIndex(monthIndex);

      applyMonthSlice(monthToShow, selectedAcademicYear, yearConfs, { replaceAllMonths: true });
    } catch (error) {
      setError("Failed to fetch conferences");
      setAvailableMonths([]);
      setCurrentMonth("");
      setCurrentMonthIndex(0);
      setConferencesByMonth({});
      setLoadedMonths([]);
    } finally {
      setLoading(false);
    }
  };

  const initialConferencesFetchRef = useRef(false);
  useEffect(() => {
    if (initialConferencesFetchRef.current) return;
    initialConferencesFetchRef.current = true;
    fetchInitialConferences();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selected && currentMonth) {
      const monthConfs = conferencesByMonth[currentMonth] || [];
      const dates = getDatesInMonth(currentMonth);
      const scheduledDates = dates.filter(date => {
        const conferencesForDate = getConferencesForDate(date, monthConfs);
        return conferencesForDate.length > 0;
      });
      if (scheduledDates.length > 0) {
        setSelected(getClosestScheduledDate(scheduledDates));
      }
    }
    // eslint-disable-next-line
  }, [currentMonth, conferencesByMonth]);

  // Show loading or empty state
  if (loading) {
    return (
      <div className="min-vh-100">
        <Navbar pageTitle="Conference Scheduler" />
        
        <div className="bg-white shadow-sm">
          <Container>
            <Row>
              <Col className="d-flex py-lg-0 py-lg-3 py-md-3 py-3 align-items-center justify-content-start">
                <h6 className="mb-0 fw-semibold">
                  Conferences
                </h6>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner 
              animation="border" 
              variant="primary" 
              size="lg"
              className="mb-3"
            />
            <div className="text-muted">Loading conference schedules...</div>
          </div>
        </Container>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-vh-100">
        <Navbar />
        <Container className="mt-4">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </Container>
      </div>
    );
  }

  // Handle month navigation (reset selected date)
  const handleMonthNavigation = (direction) => {
    let newIndex = currentMonthIndex;
    if (direction === "next" && currentMonthIndex < availableMonths.length - 1) {
      newIndex = currentMonthIndex + 1;
    } else if (direction === "prev" && currentMonthIndex > 0) {
      newIndex = currentMonthIndex - 1;
    }
    const newMonth = availableMonths[newIndex];
    setCurrentMonthIndex(newIndex);
    setCurrentMonth(newMonth);
    let monthConfs = conferencesByMonth[newMonth];
    if (!loadedMonths.includes(newMonth)) {
      monthConfs = applyMonthSlice(newMonth, selectedAcademicYear, currentYearConferences);
    }
    monthConfs = monthConfs || [];
    // Reset selected date to first scheduled date in new month
    const dates = getDatesInMonth(newMonth);
    const scheduledDates = dates.filter(date => {
      const conferencesForDate = getConferencesForDate(date, monthConfs);
      return conferencesForDate.length > 0;
    });
    setSelected(scheduledDates.length > 0 ? getClosestScheduledDate(scheduledDates) : null);
    setSelectedDate(null);
  };

  // Handle academic year change
  const handleAcademicYearChange = async (year) => {
    const yearInt = parseInt(year);
    setSelectedAcademicYear(yearInt);

    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem('token');
      if (!token) {
        setError("Please log in to view conferences");
        return;
      }

      const allResponse = await axios.get(`${API_ENDPOINTS.CONFERENCES}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allConfs = allResponse.data || [];
      setAllConferences(allConfs);
      setAvailableAcademicYears(buildAvailableAcademicYears(allConfs));

      const yearConfs = filterConferencesByAcademicYear(allConfs, yearInt);
      setCurrentYearConferences(yearConfs);
      setConferencesByMonth({});
      setLoadedMonths([]);

      const userRole = localStorage.getItem('role');
      const shouldShowCurrentMonth =
        ['director', 'resident', 'attending'].includes(userRole) || hasModuleAccess('conferenceScheduler');

      const allMonths = getAllMonths(yearConfs);
      setAvailableMonths(allMonths);

      if (allMonths.length === 0) {
        setCurrentMonthIndex(0);
        setCurrentMonth("");
        setConferencesByMonth({});
        setLoadedMonths([]);
        return;
      }

      const { month: monthToShow, index: monthIndex } = pickDefaultMonth(
        allMonths,
        yearInt,
        shouldShowCurrentMonth
      );

      setCurrentMonthIndex(monthIndex);
      setCurrentMonth(monthToShow);

      applyMonthSlice(monthToShow, yearInt, yearConfs, { replaceAllMonths: true });
    } catch (error) {
      console.error("Error changing academic year:", error);
      setError("Failed to change academic year");
    } finally {
      setLoading(false);
    }
  };

  // Handle date click
  const handleDateClick = async (date) => {
    setSelectedDate(date);
    setShowDetailsModal(true);
  };

  // Handle session click
  const handleSessionClick = async (session) => {
    setSelectedSession(session);
    setShowAttendanceTracker(true);
    // Fetch attendance data for this session
    await fetchAttendanceData(session.id);
  };

  // Handle conference creation
  const handleConferenceCreated = () => {
    setShowSchedulingModal(false);
    fetchInitialConferences(); // Refresh the data
  };

  // Export to calendar handler
  const handleExportToCalendar = async () => {
    try {
      setExportLoading(true);
      setExportMessage("");
      setExportError("");
      
      // Get current user info to check if they're allowed to export
      const canExport = isDirector;

      if (!canExport) {
        setExportError("Only directors (or users with active delegation) can export conference sessions to calendars");
        setTimeout(() => setExportError(""), 5000);
        return;
      }
      
      // Check if current month is selected
      if (!currentMonth) {
        setExportError("No month selected for export");
        setTimeout(() => setExportError(""), 5000);
        return;
      }
      
      // Get conferences for the current academic year
      const yearConfs = getConferencesForYear(Object.values(conferencesByMonth).flat(), selectedAcademicYear);
      
      if (yearConfs.length === 0) {
        setExportError("No conferences found for the current academic year");
        setTimeout(() => setExportError(""), 5000);
        return;
      }
      
      // Get conferences for the current month only
      const currentMonthConfs = [];
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Set to start of day for comparison
      
      yearConfs.forEach(conf => {
        if (conf.sessions) {
          conf.sessions.forEach(session => {
            if (session.date && session.date.startsWith(currentMonth) && !session.isCancelled) {
              // Check if the session date has not passed
              const sessionDate = new Date(session.date);
              sessionDate.setHours(0, 0, 0, 0);
              
              if (sessionDate >= now) {
                currentMonthConfs.push({
                  ...conf,
                  session
                });
              }
            }
          });
        }
      });
      
      if (currentMonthConfs.length === 0) {
        setExportError("No future conference sessions found for the selected month");
        setTimeout(() => setExportError(""), 5000);
        return;
      }
      
      // Collect all unique attendee IDs from conferences in the current month
      const allAttendeeIds = new Set();
      currentMonthConfs.forEach(conf => {
        if (conf.requiredAttendees) {
          conf.requiredAttendees.forEach(id => allAttendeeIds.add(id));
        }
      });
      
      if (allAttendeeIds.size === 0) {
        setExportError("No attendees found for conferences in the selected month");
        setTimeout(() => setExportError(""), 5000);
        return;
      }
      
      // Call the bulk export endpoint with current month only
      const response = await axios.post(
        `${API_ENDPOINTS.CONFERENCE_SESSIONS}/bulk-export-calendar`,
        {
          monthRange: { startMonth: currentMonth, endMonth: currentMonth },
          attendeeIds: Array.from(allAttendeeIds),
          academicYear: selectedAcademicYear
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        }
      );
      
      const { eventsCreated, eventsDeleted, failedExports, sessionsProcessed, attendeesProcessed } = response.data;
      
      let message = `Successfully exported conference sessions to calendars!\n`;
      message += `• ${eventsCreated} events created\n`;
      message += `• ${eventsDeleted} prior events deleted\n`;
      message += `• ${sessionsProcessed} sessions processed\n`;
      message += `• ${attendeesProcessed} attendees updated`;
      
      if (failedExports > 0) {
        message += `\n• ${failedExports} exports failed`;
      }
      
      setExportMessage(message);
      setTimeout(() => setExportMessage(""), 8000);
      
    } catch (error) {
      console.error("Error exporting to calendar:", error);
      const errorMessage = error.response?.data?.error || "Failed to export to calendar";
      setExportError(errorMessage);
      setTimeout(() => setExportError(""), 5000);
    } finally {
      setExportLoading(false);
    }
  };

  // Handle export confirmation
  const handleExportClick = () => {
    // Get export details for the modal
    const yearConfs = getConferencesForYear(Object.values(conferencesByMonth).flat(), selectedAcademicYear);
    const allAttendeeIds = new Set();
    let totalSessions = 0;
    
    yearConfs.forEach(conf => {
      if (conf.requiredAttendees) {
        conf.requiredAttendees.forEach(id => allAttendeeIds.add(id));
      }
      if (conf.sessions) {
        totalSessions += conf.sessions.filter(s => !s.isCancelled).length;
      }
    });
    
    setShowExportModal(true);
  };

  // Get export summary for modal
  const getExportSummary = () => {
    const yearConfs = getConferencesForYear(Object.values(conferencesByMonth).flat(), selectedAcademicYear);
    const allAttendeeIds = new Set();
    let totalSessions = 0;
    // Only include non-cancelled conferences and sessions
    const currentMonthConfs = [];
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of day for comparison
    yearConfs.forEach(conf => {
      if (conf.sessions) {
        conf.sessions.forEach(session => {
          if (
            session.date &&
            session.date.startsWith(currentMonth) &&
            !session.isCancelled // Only include non-cancelled
          ) {
            const sessionDate = new Date(session.date);
            sessionDate.setHours(0, 0, 0, 0);
            if (sessionDate >= now) {
              currentMonthConfs.push({
                ...conf,
                session
              });
              totalSessions++;
            }
          }
        });
      }
    });
    // Collect attendee IDs from current month conferences only
    currentMonthConfs.forEach(conf => {
      if (conf.requiredAttendees) {
        conf.requiredAttendees.forEach(id => allAttendeeIds.add(id));
      }
    });
    // Calculate unique conferences (by title+type) in the current month
    const uniqueConfs = [];
    const seen = new Set();
    currentMonthConfs.forEach(conf => {
      const key = conf.title + '|' + conf.type;
      if (!seen.has(key)) {
        uniqueConfs.push(conf);
        seen.add(key);
      }
    });
    // Format current month for display
    const [year, month] = currentMonth.split("-");
    const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    return {
      conferences: uniqueConfs.length,
      sessions: totalSessions,
      attendees: allAttendeeIds.size,
      month: monthName,
      monthRange: currentMonth
    };
  };

  // Build a flattened list of all conference documents for the selected academic year
  const getConferenceDocuments = () => {
    // Prefer the full-year conferences list for the selected academic year,
    // so the month navigation does not affect which documents are visible.
    const baseConfs = currentYearConferences && currentYearConferences.length
      ? currentYearConferences
      : getConferencesForYear(Object.values(conferencesByMonth).flat(), selectedAcademicYear);
    const yearConfs = baseConfs || [];
    const documents = [];

    yearConfs.forEach((conf) => {
      (conf.sessions || []).forEach((session) => {
        if (!Array.isArray(session.attachments) || session.attachments.length === 0) return;

        session.attachments.forEach((att, index) => {
          const resolvePath = (a) => {
            if (typeof a === 'string') return a;
            if (a && a.url) return a.url;
            if (a && a.path) return a.path;
            return '';
          };

          const attachmentPath = resolvePath(att);
          const fileName = (() => {
            try {
              return (attachmentPath || '').split('/').pop() || `file-${index + 1}`;
            } catch {
              return `file-${index + 1}`;
            }
          })();

          const fileSize = att && (att.size || att.fileSize);
          const mimeType = (att && att.mimeType) || '';
          const typeLabel = getDocumentTypeLabel(fileName, mimeType);

          documents.push({
            id: `${conf.id || conf._id || conf.title}-${session.id || session._id || session.date}-${index}`,
            conferenceTitle: conf.title,
            conferenceType: conf.type,
            conferenceTypeRaw: conf.type,
            academicYear: conf.academicYear,
            sessionDate: session.date,
            sessionId: session.id || session._id,
            attachmentPath,
            fileName,
            fileSize,
            mimeType,
            typeLabel
          });
        });
      });
    });

    // Sort newest first by session date
    return documents.sort((a, b) => {
      const aDate = a.sessionDate ? new Date(a.sessionDate) : 0;
      const bDate = b.sessionDate ? new Date(b.sessionDate) : 0;
      return bDate - aDate;
    });
  };

  const isWithinDateRange = (sessionDateStr, rangeKey) => {
    if (!rangeKey || rangeKey === 'all') return true;
    if (!sessionDateStr) return false;

    try {
      const sessionDate = new Date(`${sessionDateStr}T00:00:00`);
      if (isNaN(sessionDate.getTime())) return false;

      const now = new Date();
      const diffMs = Math.abs(now.getTime() - sessionDate.getTime());
      const diffDays = diffMs / (1000 * 60 * 60 * 24);

      switch (rangeKey) {
        case '1w':
          return diffDays <= 7;
        case '2w':
          return diffDays <= 14;
        case '1m':
          return diffDays <= 30;
        case '3m':
          return diffDays <= 90;
        case '6m':
          return diffDays <= 180;
        case '1y':
          return diffDays <= 365;
        default:
          return true;
      }
    } catch (error) {
      console.error('Error checking document date range:', error);
      return false;
    }
  };

  // Handle export confirmation
  const handleExportConfirm = async () => {
    setShowExportModal(false);
    await handleExportToCalendar();
  };

  const renderDocumentsView = () => {
    const allDocuments = getConferenceDocuments();

    if (!allDocuments.length) {
      return (
        <Card className="mb-4 shadow-sm border-0 py-4">
          <CardBody className="p-0">
            <div className="text-center py-4">
              <div className="d-flex flex-column align-items-center gap-2">
                <i className="bi bi-folder2-open fs-1 text-muted"></i>
                <h5>No Conference Documents</h5>
                <p className="text-muted mb-0">
                  There are no uploaded documents for conferences in the selected academic year.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      );
    }

    const typeOptions = Array.from(new Set(allDocuments.map((d) => d.typeLabel))).sort();
    const conferenceTypeOptions = Array.from(
      new Set(
        allDocuments
          .map((d) => d.conferenceTypeRaw)
          .filter((v) => v && String(v).trim().length > 0)
      )
    ).sort();

    const filteredDocuments = allDocuments.filter((doc) => {
      if (selectedDocumentType !== 'all' && doc.typeLabel !== selectedDocumentType) {
        return false;
      }

      if (selectedConferenceType !== 'all') {
        const typeRaw = (doc.conferenceTypeRaw || '').toString().toLowerCase();
        if (typeRaw !== selectedConferenceType.toLowerCase()) {
          return false;
        }
      }

      if (!isWithinDateRange(doc.sessionDate, selectedDocumentDateRange)) {
        return false;
      }

      if (documentsSearch && documentsSearch.trim()) {
        const search = documentsSearch.toLowerCase();
        const haystack = [
          doc.fileName,
          doc.conferenceTitle,
          doc.conferenceType,
          doc.sessionDate
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      return true;
    });

    const prettySize = (bytes) => {
      const n = Number(bytes);
      if (!n || isNaN(n)) return '';
      const kb = n / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      const mb = kb / 1024;
      return `${mb.toFixed(2)} MB`;
    };

    const handleDownload = async (doc) => {
      try {
        const token = localStorage.getItem('token');
        const path = doc.attachmentPath || '';
        const url = path.startsWith('http')
          ? path
          : path.startsWith('/uploads/')
          ? `${API_BASE_URL}${path}`
          : `${API_BASE_URL}/uploads/${path}`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/octet-stream'
          }
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = doc.fileName || 'attachment';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Error downloading conference document:', error);
        alert('Failed to download document. Please try again.');
      }
    };

    return (
      <Row className="g-3">
        {/* Sidebar menu – similar to Onboarding "My" tab */}
        <Col lg={2} md={3} className="d-none d-md-block mb-3">
          <Card className="bg-white shadow-sm border-0 p-0 py-2 custom-sticky-top" style={{ top: '5rem' }}>
            <CardBody className="pe-0 ps-2 pt-0 pb-2">
              <Nav variant="pills" className="flex-column">
                <Nav.Item>
                  <Nav.Link className="disabled fw-semibold">Menu</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="allDocs"
                    active={selectedDocumentType === 'all'}
                    onClick={() => setSelectedDocumentType('all')}
                  >
                    All Documents
                  </Nav.Link>
                </Nav.Item>
                {typeOptions.map((type) => (
                  <Nav.Item key={type}>
                    <Nav.Link
                      eventKey={`type-${type}`}
                      active={selectedDocumentType === type}
                      onClick={() => setSelectedDocumentType(type)}
                    >
                      {type}
                    </Nav.Link>
                  </Nav.Item>
                ))}
                {conferenceTypeOptions.length > 0 && (
                  <>
                    <Nav.Item className="mt-2">
                      <Nav.Link className="disabled fw-semibold small">
                        Conference Type
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="conf-all"
                        active={selectedConferenceType === 'all'}
                        onClick={() => setSelectedConferenceType('all')}
                      >
                        All Types
                      </Nav.Link>
                    </Nav.Item>
                    {conferenceTypeOptions.map((ct) => (
                      <Nav.Item key={ct}>
                        <Nav.Link
                          eventKey={`conf-${ct}`}
                          active={selectedConferenceType.toLowerCase() === ct.toLowerCase()}
                          onClick={() => setSelectedConferenceType(ct)}
                        >
                          {ct}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </>
                )}
              </Nav>
            </CardBody>
          </Card>
        </Col>

        {/* Mobile menu accordion */}
        <Col md={12} className="d-md-none mb-3">
          <Card className="bg-white shadow-sm border-0 p-0">
            <Accordion>
              <Accordion.Item eventKey="menu" className="border-0 custom-accordian-border">
                <Accordion.Header>
                  <span className="fw-semibold">Menu</span>
                </Accordion.Header>
                <Accordion.Body className="p-0">
                  <Nav variant="pills" className="flex-column mb-2">
                    <Nav.Item>
                      <Nav.Link
                        eventKey="allDocs"
                        active={selectedDocumentType === 'all'}
                        onClick={() => setSelectedDocumentType('all')}
                      >
                        All Documents
                      </Nav.Link>
                    </Nav.Item>
                    {typeOptions.map((type) => (
                      <Nav.Item key={type}>
                        <Nav.Link
                          eventKey={`type-${type}`}
                          active={selectedDocumentType === type}
                          onClick={() => setSelectedDocumentType(type)}
                        >
                          {type}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                    {conferenceTypeOptions.length > 0 && (
                      <>
                        <Nav.Item className="mt-2">
                          <Nav.Link className="disabled fw-semibold small">
                            Conference Type
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link
                            eventKey="conf-all"
                            active={selectedConferenceType === 'all'}
                            onClick={() => setSelectedConferenceType('all')}
                          >
                            All Types
                          </Nav.Link>
                        </Nav.Item>
                        {conferenceTypeOptions.map((ct) => (
                          <Nav.Item key={ct}>
                            <Nav.Link
                              eventKey={`conf-${ct}`}
                              active={selectedConferenceType.toLowerCase() === ct.toLowerCase()}
                              onClick={() => setSelectedConferenceType(ct)}
                            >
                              {ct}
                            </Nav.Link>
                          </Nav.Item>
                        ))}
                      </>
                    )}
                  </Nav>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Card>
        </Col>

        {/* Main content – styled like My Assignments list */}
        <Col lg={10} md={9} sm={12}>
          <div className="bg-inverse-light rounded-3 shadow-sm">
            <div className="d-flex flex-column flex-md-row align-items-md-center align-items-start justify-content-between gap-2 p-3 px-4">
              <div>
                <h6 className="fw-semibold mb-0">Conference Documents</h6>
                <p className="text-muted small mb-0">
                  Showing {filteredDocuments.length} document
                  {filteredDocuments.length === 1 ? '' : 's'} for the selected academic year.
                </p>
              </div>
                <div className="d-flex flex-row gap-2">
                  <Form.Group>
                  <Form.Control
                    type="search"
                    placeholder="Search by document name, conference, or date..."
                    value={documentsSearch}
                    onChange={(e) => setDocumentsSearch(e.target.value)}
                    size="sm"
                  /></Form.Group>
                  <Form.Group>
                    <Form.Select
                      size="sm"
                      value={selectedDocumentDateRange}
                      onChange={(e) => setSelectedDocumentDateRange(e.target.value)}
                    
                    >
                      <option value="all">All dates</option>
                      <option value="1w">Within 1 week</option>
                      <option value="2w">Within 2 weeks</option>
                      <option value="1m">Within 1 month</option>
                      <option value="3m">Within 3 months</option>
                      <option value="6m">Within 6 months</option>
                      <option value="1y">Within 1 year</option>
                    </Form.Select>
                  </Form.Group>
                </div>
            </div>

            <div className="px-3 pb-3">
              {filteredDocuments.length === 0 ? (
                <div className="text-center py-5">
                  <div className="text-muted mb-2 bg-secondary rounded-2 p-2" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                    <i className="bi bi-inbox fs-2"></i>
                  </div>
                  <h6 className="text-muted mb-1">No documents match your filters</h6>
                  <small className="text-muted">
                    Try adjusting the document type, date range, or search term.
                  </small>
                </div>
              ) : (
                filteredDocuments.map((doc) => {
                  const sessionDateLabel = doc.sessionDate
                    ? new Date(doc.sessionDate + 'T00:00:00').toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    : 'Date not specified';

                  return (
                    <div
                      key={doc.id}
                      className="border-alt-light custom-hover-bg-alt rounded-3 mb-2 p-3 gap-2 d-flex flex-md-row flex-column justify-content-md-between justify-content-start align-items-md-center align-items-start"
                    >
                      <div className="d-flex flex-row align-items-start gap-3">
                        <Badge bg="secondary" className="rounded-2 p-2">
                          <i className={`${getFileIcon(doc.fileName, doc.mimeType)} fs-4`}></i>
                        </Badge>
                        <div className="d-flex flex-column align-items-start">
                          <span className="fw-semibold">{doc.fileName}</span>
                          <div className="d-flex flex-wrap align-items-center gap-2 small">
                            {/* <Badge bg="light" text="dark">
                              {doc.typeLabel}
                            </Badge> */}
                            {doc.fileSize && (
                              <span className="text-muted">{prettySize(doc.fileSize)}</span>
                            )}
                          </div>
                          <small className="text-muted small">
                            {sessionDateLabel} • {doc.conferenceTitle}
                            {doc.conferenceType && ` • ${doc.conferenceType}`}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex flex-row align-items-center gap-2 mt-2 mt-md-0">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleDownload(doc)}
                        >
                          <i className="bi bi-download me-1"></i>
                          Download
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  // Generate email HTML content (same as print but without print button)
  const generateEmailHTML = () => {
    if (!currentMonth) {
      return null;
    }

    const monthConfs = conferencesByMonth[currentMonth] || [];
    if (monthConfs.length === 0) {
      return null;
    }

    const [year, month] = currentMonth.split("-");
    const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const dates = getDatesInMonth(currentMonth);
    
    // Group conferences by date
    const conferencesByDate = {};
    dates.forEach(date => {
      const dateStr = date.toISOString().split('T')[0];
      const confsForDate = getConferencesForDate(date, monthConfs);
      if (confsForDate.length > 0) {
        conferencesByDate[dateStr] = confsForDate;
      }
    });

    // Get base URL and token for images
    const baseUrl = window.location.origin;
    const token = localStorage.getItem('token');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Conference Schedule - ${monthName}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              margin: 0;
              padding: 20px;
              display: flex;
              justify-content: center;
            }
            .print-container {
              max-width: 800px;
              width: 100%;
              margin: 0 auto;
            }
            .print-header-container {
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
              border-bottom: 2px solid #000;
              margin-bottom: 10px;
            }
            .logo-container {
              text-align: center;
              margin-bottom: 10px;
              padding-bottom: 15px;
            }
            .logo-container img {
              height: 40px;
              max-width: 200px;
            }
            .print-header {
              text-align: center;
              margin-bottom: 10px;
              padding-bottom: 15px;
            }
            .print-header h1 {
              margin: 10px 0 5px 0;
              font-size: 24px;
            }
            .print-header .subtitle {
              font-size: 14px;
              color: #666;
            }
            .date-section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .date-header {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 10px;
              padding: 8px;
              background-color: #eaedf2;
              border-radius: 0 6px 6px 0;
              border-left: 4px solid #00a0d4;
            }
            .conference-item {
              margin-bottom: 15px;
              padding: 10px;
              border: 1px solid #d4e0ed;
              border-radius: 4px;
              page-break-inside: avoid;
              display: flex;
              gap: 15px;
            }
            .conference-content {
              flex: 1;
              min-width: 0;
            }
            .conference-image {
              flex-shrink: 0;
              width: 120px;
              text-align: right;
            }
            .conference-image img {
              max-width: 120px;
              max-height: 120px;
              border-radius: 4px;
              border: 1px solid #d4e0ed;
              object-fit: cover;
            }
            .conference-title {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .conference-time {
              color: #666;
              font-size: 11px;
              margin-bottom: 5px;
            }
            .conference-details {
              margin-top: 8px;
              font-size: 11px;
            }
            .conference-details div {
              margin-bottom: 4px;
            }
            .conference-details strong {
              color: #333;
            }
            .badge {
              display: inline-block;
              padding: 2px 6px;
              border-radius: 3px;
              font-size: 10px;
              font-weight: bold;
              margin-left: 8px;
            }
            .badge-primary { background-color: #00a0d4; color: white; }
            .badge-success { background-color: #01D8AE; color: white; }
            .badge-warning { background-color: #F6C000; color: #000; }
            .badge-info { background-color: #00dbff; color: white; }
            .badge-danger { background-color: #F8285A; color: white; }
            .no-conferences {
              text-align: center;
              padding: 20px;
              color: #999;
              font-style: italic;
            }
            .print-footer {
              margin-top: 40px;
              padding-top: 15px;
              border-top: 1px solid #d4e0ed;
              font-size: 10px;
              color: #666;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="print-header-container">
              <div class="logo-container">
                <img src="${baseUrl}/images/asset11.svg" alt="MedHive Logo" onerror="this.style.display='none'" />
              </div>
              <div class="print-header">
                <h1>Conference Schedule</h1>
                <div class="subtitle">${monthName} - Academic Year ${selectedAcademicYear}-${parseInt(selectedAcademicYear) + 1}</div>
                <div class="subtitle">Generated: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
              </div>
            </div>
            ${Object.keys(conferencesByDate).length === 0 
              ? '<div class="no-conferences">No conferences scheduled for this month.</div>'
              : Object.keys(conferencesByDate).sort().map(dateStr => {
                  const date = new Date(dateStr + 'T00:00:00');
                  const dateLabel = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                  const confs = conferencesByDate[dateStr];
                  return `
                    <div class="date-section">
                      <div class="date-header">${dateLabel}</div>
                      ${confs.map(conf => {
                        const isCancelled = conf.session.isCancelled;
                        const timeLabel = conf.startTime && conf.endTime
                          ? `${formatTime(conf.startTime)} - ${formatTime(conf.endTime)}`
                          : conf.startTime ? `${formatTime(conf.startTime)}` : 'Time not specified';
                        const typeInfo = renderConferenceType(conf.type);
                        const sessionImage = conf.session.image || conf.image;
                        const imageUrl = sessionImage 
                          ? (sessionImage.startsWith('/uploads/') 
                              ? `${baseUrl}${sessionImage}?token=${token}` 
                              : sessionImage)
                          : null;
                        return `
                          <div class="conference-item ${isCancelled ? 'cancelled-item' : ''}">
                            <div class="conference-content">
                              <div class="conference-title">
                                ${conf.title}
                                <span class="badge" style="background:${typeInfo.color}20;color:${typeInfo.color};border:1px solid ${typeInfo.color}55;">${typeInfo.label}</span>
                                ${isCancelled ? '<span class="badge badge-danger">CANCELLED</span>' : ''}
                              </div>
                              <div class="conference-time">${timeLabel}</div>
                              <div class="conference-details">
                                ${(conf.session.location || conf.location) ? `<div><strong>Location:</strong> ${conf.session.location || conf.location}</div>` : ''}
                                ${conf.session.speaker ? `<div><strong>Speaker:</strong> ${conf.session.speaker}${conf.session.speakerTitle ? ` (${conf.session.speakerTitle})` : ''}</div>` : ''}
                                ${conf.session.talkTitle ? `<div><strong>Talk Title:</strong> ${conf.session.talkTitle}</div>` : ''}
                                ${conf.session.talkTime ? `<div><strong>Talk Time:</strong> ${formatTime(conf.session.talkTime)}</div>` : ''}
                                ${conf.session.description ? `<div><strong>Description:</strong> ${conf.session.description}</div>` : ''}
                                ${(conf.zoomLink || conf.session.zoomLink) ? `<div><strong>Zoom Link:</strong> ${conf.zoomLink || conf.session.zoomLink}</div>` : ''}
                              </div>
                            </div>
                            ${imageUrl ? `
                              <div class="conference-image">
                                <img src="${imageUrl}" alt="Conference session" onerror="this.style.display='none'" />
                              </div>
                            ` : ''}
                          </div>
                        `;
                      }).join('')}
                    </div>
                  `;
                }).join('')
            }
            <div class="print-footer">
              <div>Number of conferences: ${Object.keys(conferencesByDate).length}</div>
              <div>This document was generated from MedHive Resident Management Platform</div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  // Fetch users for email selection
  const fetchUsersForEmail = async () => {
    try {
      setUsersLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_ENDPOINTS.USERS}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle modal open - fetch users
  const handleEmailModalOpen = () => {
    setShowEmailModal(true);
    fetchUsersForEmail();
  };


  // Handle user selection toggle
  const handleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Get filtered users based on search, role, and PGY
  const getFilteredUsers = () => {
    let filtered = users;

    // Filter by search term
    if (userSearchTerm.trim()) {
      const searchLower = userSearchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        (user.firstName && user.firstName.toLowerCase().includes(searchLower)) ||
        (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
        (user.email && user.email.toLowerCase().includes(searchLower))
      );
    }

    // Filter by role
    if (selectedRole) {
      filtered = filtered.filter(user => user.role === selectedRole);
    }

    // Filter by PGY level (only for residents)
    if (selectedRole === 'resident' && selectedPGY) {
      filtered = filtered.filter(user => user.year === parseInt(selectedPGY));
    }

    return filtered;
  };

  // Handle send email
  const handleSendEmail = async () => {
    // Collect all email addresses
    const emailAddresses = [];
    
    // Add manual email if provided
    if (emailAddress && emailAddress.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailAddress.trim())) {
        alert('Please enter a valid email address');
        return;
      }
      emailAddresses.push(emailAddress.trim());
    }

    // Add selected user emails
    const selectedUsers = users.filter(user => selectedUserIds.includes(user.id));
    selectedUsers.forEach(user => {
      if (user.email && !emailAddresses.includes(user.email)) {
        emailAddresses.push(user.email);
      }
    });

    if (emailAddresses.length === 0) {
      alert('Please select at least one user or enter an email address');
      return;
    }

    const htmlContent = generateEmailHTML();
    if (!htmlContent) {
      alert('No conferences scheduled for this month');
      return;
    }

    try {
      setEmailLoading(true);
      const [year, month] = currentMonth.split("-");
      const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const subject = `Conference Schedule - ${monthName} - Academic Year ${selectedAcademicYear}-${parseInt(selectedAcademicYear) + 1}`;

      const response = await axios.post(
        `${API_ENDPOINTS.CONFERENCES}/send-email`,
        {
          emails: emailAddresses,
          htmlContent,
          subject
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      alert(`Email sent successfully to ${emailAddresses.length} recipient(s)!`);
      setShowEmailModal(false);
      setEmailAddress("");
      setSelectedUserIds([]);
      setUserSearchTerm("");
    } catch (error) {
      console.error('Error sending email:', error);
      alert(error.response?.data?.error || 'Failed to send email. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  // Handle print - create printable version of all sessions in the month
  const handlePrint = () => {
    if (!currentMonth) {
      alert('No month selected');
      return;
    }

    const monthConfs = conferencesByMonth[currentMonth] || [];
    if (monthConfs.length === 0) {
      alert('No conferences scheduled for this month');
      return;
    }

    const [year, month] = currentMonth.split("-");
    const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const dates = getDatesInMonth(currentMonth);
    
    // Group conferences by date
    const conferencesByDate = {};
    dates.forEach(date => {
      const dateStr = date.toISOString().split('T')[0];
      const confsForDate = getConferencesForDate(date, monthConfs);
      if (confsForDate.length > 0) {
        conferencesByDate[dateStr] = confsForDate;
      }
    });

    // Get base URL and token for images
    const baseUrl = window.location.origin;
    const token = localStorage.getItem('token');
    const logoUrl = `${baseUrl}/images/asset11.svg`;
    
    const content = Object.keys(conferencesByDate).length === 0 
      ? '<div class="no-conferences">No conferences scheduled for this month.</div>'
      : Object.keys(conferencesByDate).sort().map(dateStr => {
          const date = new Date(dateStr + 'T00:00:00');
          const dateLabel = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
          const confs = conferencesByDate[dateStr];
          return `
            <div class="date-section">
              <div class="date-header">${dateLabel}</div>
              ${confs.map(conf => {
                const isCancelled = conf.session.isCancelled;
                const timeLabel = conf.startTime && conf.endTime
                  ? `${formatTime(conf.startTime)} - ${formatTime(conf.endTime)}`
                  : conf.startTime ? `${formatTime(conf.startTime)}` : 'Time not specified';
                const typeInfo = renderConferenceType(conf.type);
                const sessionImage = conf.session.image || conf.image;
                const imageUrl = sessionImage 
                  ? (sessionImage.startsWith('/uploads/') 
                      ? `${baseUrl}${sessionImage}?token=${token}` 
                      : sessionImage)
                  : null;
                return `
                  <div class="conference-item" style="${isCancelled ? 'opacity: 0.6; text-decoration: line-through;' : ''}">
                    <div class="conference-content">
                      <div class="conference-title">
                        ${conf.title}
                        <span class="badge" style="background:${typeInfo.color}20;color:${typeInfo.color};border:1px solid ${typeInfo.color}55;">${typeInfo.label}</span>
                        ${isCancelled ? '<span class="badge badge-danger">CANCELLED</span>' : ''}
                      </div>
                      <div class="conference-time">${timeLabel}</div>
                      <div class="conference-details">
                        ${(conf.session.location || conf.location) ? `<div><strong>Location:</strong> ${conf.session.location || conf.location}</div>` : ''}
                        ${conf.session.speaker ? `<div><strong>Speaker:</strong> ${conf.session.speaker}${conf.session.speakerTitle ? ` (${conf.session.speakerTitle})` : ''}</div>` : ''}
                        ${conf.session.talkTitle ? `<div><strong>Talk Title:</strong> ${conf.session.talkTitle}</div>` : ''}
                        ${conf.session.talkTime ? `<div><strong>Talk Time:</strong> ${formatTime(conf.session.talkTime)}</div>` : ''}
                        ${conf.session.description ? `<div><strong>Description:</strong> ${conf.session.description}</div>` : ''}
                        ${(conf.zoomLink || conf.session.zoomLink) ? `<div><strong>Zoom Link:</strong> ${conf.zoomLink || conf.session.zoomLink}</div>` : ''}
                      </div>
                    </div>
                    ${imageUrl ? `
                      <div class="conference-image">
                        <img src="${imageUrl}" alt="Conference session" onerror="this.style.display='none'" />
                      </div>
                    ` : ''}
                  </div>
                `;
              }).join('')}
            </div>
          `;
        }).join('');

    const customStyles = `
      .date-section {
        margin-bottom: 30px;
        page-break-inside: avoid;
      }
      .date-header {
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 10px;
        padding: 8px;
        background-color: #eaedf2;
        border-radius: 0 6px 6px 0;
        border-left: 4px solid #00a0d4;
      }
      .conference-item {
        margin-bottom: 15px;
        padding: 10px;
        border: 1px solid #d4e0ed;
        border-radius: 4px;
        page-break-inside: avoid;
        display: flex;
        gap: 15px;
      }
      .conference-content {
        flex: 1;
        min-width: 0;
      }
      .conference-image {
        flex-shrink: 0;
        width: 120px;
        text-align: right;
      }
      .conference-image img {
        max-width: 120px;
        max-height: 120px;
        border-radius: 4px;
        border: 1px solid #d4e0ed;
        object-fit: cover;
      }
      .conference-title {
        font-size: 14px;
        font-weight: bold;
        margin-bottom: 5px;
      }
      .conference-time {
        color: #666;
        font-size: 11px;
        margin-bottom: 5px;
      }
      .conference-details {
        margin-top: 8px;
        font-size: 11px;
      }
      .conference-details div {
        margin-bottom: 4px;
      }
      .conference-details strong {
        color: #333;
      }
      .badge {
        display: inline-block;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: bold;
        margin-left: 8px;
      }
      .badge-primary { background-color: #00a0d4; color: white; }
      .badge-success { background-color: #01D8AE; color: white; }
      .badge-warning { background-color: #F6C000; color: #000; }
      .badge-info { background-color: #00dbff; color: white; }
      .badge-danger { background-color: #F8285A; color: white; }
      .no-conferences {
        text-align: center;
        padding: 20px;
        color: #999;
        font-style: italic;
      }
    `;

    const footerContent = `
      <div>Number of conferences: ${Object.keys(conferencesByDate).length}</div>
      <div>This document was generated from MedHive Resident Management Platform</div>
    `;

    const printContent = buildPrintDocument({
      title: `Conference Schedule - ${monthName}`,
      content,
      headerTitle: 'Conference Schedule',
      headerSubtitle: `${monthName} - Academic Year ${selectedAcademicYear}-${parseInt(selectedAcademicYear) + 1}`,
      logoUrl,
      showPrintButton: true,
      additionalHeaderInfo: [
        { label: 'Generated', value: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) }
      ],
      footerMessage: footerContent,
      customStyles
    });

    openPrintWindow(printContent);
  };

  // Delete conference handler
  const handleDeleteConferences = async () => {
    try {
      setDeleteLoading(true);
      const token = localStorage.getItem('token');
      
      if (deleteType === "current") {
        // Delete conferences for the current selected date
        const activeDate = selectedDate || selected;
        if (!activeDate) {
          setExportError("No date selected for deletion");
          setTimeout(() => setExportError(""), 3000);
          return;
        }
        
        const yearConfs = getConferencesForYear(Object.values(conferencesByMonth).flat(), selectedAcademicYear);
        const selectedConferences = getConferencesForDate(activeDate, yearConfs);
        
        if (selectedConferences.length === 0) {
          setExportError("No conferences found for the selected date");
          setTimeout(() => setExportError(""), 3000);
          return;
        }
        
        // Delete each conference for the current date
        for (const conf of selectedConferences) {
          await axios.delete(`${API_ENDPOINTS.CONFERENCES}/${conf.id}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
        
        setExportMessage(`Successfully deleted ${selectedConferences.length} conference(s) for ${activeDate.toLocaleDateString()}`);
        
      } else if (deleteType === "all") {
        // Delete all conferences for the current academic year
        const yearConfs = getConferencesForYear(Object.values(conferencesByMonth).flat(), selectedAcademicYear);
        
        if (yearConfs.length === 0) {
          setExportError("No conferences found for the current academic year");
          setTimeout(() => setExportError(""), 3000);
          return;
        }
        
        // Delete all conferences for the academic year
        for (const conf of yearConfs) {
          await axios.delete(`${API_ENDPOINTS.CONFERENCES}/${conf.id}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
        // Delete all calendar events for this academic year
        try {
          const res = await axios.delete(`${API_ENDPOINTS.CONFERENCE_SESSIONS_DELETE_EVENTS_FOR_YEAR}/${selectedAcademicYear}/events`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setExportMessage(`Successfully deleted all ${yearConfs.length} conference(s) and ${res.data.message}`);
        } catch (err) {
          setExportMessage(`Successfully deleted all ${yearConfs.length} conference(s) for academic year ${selectedAcademicYear}-${selectedAcademicYear + 1}, but failed to delete calendar events.`);
        }
      } else if (deleteType === "series" && selectedConference) {
        // Delete all conferences in the same series (same title and type)
        const yearConfs = getConferencesForYear(Object.values(conferencesByMonth).flat(), selectedAcademicYear);
        const seriesConferences = yearConfs.filter(conf => 
          conf.title === selectedConference.title && conf.type === selectedConference.type
        );
        
        if (seriesConferences.length === 0) {
          setExportError("No conferences found in this series");
          setTimeout(() => setExportError(""), 3000);
          return;
        }
        
        // Delete all conferences in the series
        for (const conf of seriesConferences) {
          await axios.delete(`${API_ENDPOINTS.CONFERENCES}/${conf.id}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
        // Delete all future calendar events for this series
        try {
          const res = await axios.delete(`${API_ENDPOINTS.CONFERENCE_SESSIONS}/series/${selectedAcademicYear}/${encodeURIComponent(selectedConference.title)}/${encodeURIComponent(selectedConference.type)}/events`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setExportMessage(`Successfully deleted entire series: ${selectedConference.title} (${seriesConferences.length} events).\n${res.data.message}`);
        } catch (err) {
          setExportMessage(`Successfully deleted entire series: ${selectedConference.title} (${seriesConferences.length} events).\nFailed to delete future calendar events.`);
        }
      }
      
      // Reset selected date and refresh data
      setSelectedDate(null);
      setSelected(null);
      await fetchInitialConferences();
      
    } catch (error) {
      console.error("Error deleting conferences:", error);
      setExportError("Failed to delete conferences");
      setTimeout(() => setExportError(""), 3000);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setDeleteType("");
      setSelectedConference(null);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (type, conference = null) => {
    if (type === "single" && conference) {
      // Handle single conference deletion directly
      handleDeleteSingleConference(conference);
    } else if (type === "series" && conference) {
      // Handle series deletion
      setDeleteType("series");
      setSelectedConference(conference);
      setShowDeleteModal(true);
    } else {
      setDeleteType(type);
      setShowDeleteModal(true);
    }
  };

  // Handle hard delete of a single conference session
  const handleHardDeleteSingleConference = async (conference) => {
    try {
      setDeleteLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_ENDPOINTS.CONFERENCE_SESSIONS}/${conference.session.id}/hard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExportMessage('Session deleted successfully');
      setTimeout(() => setExportMessage(''), 4000);
      await fetchInitialConferences();
    } catch (error) {
      console.error('Error deleting conference session:', error);
      setExportError('Failed to delete conference session');
      setTimeout(() => setExportError(''), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle single conference cancellation
  const handleDeleteSingleConference = async (conference) => {
    try {
      setDeleteLoading(true);
      const token = localStorage.getItem('token');
      
      // Store conference info before cancellation for display
      const cancelledConferenceInfo = {
        title: conference.title,
        type: conference.type,
        date: conference.session.date,
        attendanceCode: conference.session.attendanceCode,
        talkTitle: conference.session.talkTitle,
        speaker: conference.session.speaker,
        speakerTitle: conference.session.speakerTitle,
        talkTime: conference.session.talkTime
      };
      
      // Cancel the specific session (mark as cancelled)
      const response = await axios.delete(`${API_ENDPOINTS.CONFERENCE_SESSIONS}/${conference.session.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Display cancellation message with event details
      const cancellationMessage = `Conference Cancelled: ${cancelledConferenceInfo.title} on ${new Date(cancelledConferenceInfo.date).toLocaleDateString()}`;
      setExportMessage(cancellationMessage);
      setTimeout(() => setExportMessage(""), 8000); // Show for longer since it's important
      
      // Reset selected date and refresh data
      setSelectedDate(null);
      setSelected(null);
      await fetchInitialConferences();
      
    } catch (error) {
      console.error("Error cancelling conference session:", error);
      setExportError("Failed to cancel conference session");
      setTimeout(() => setExportError(""), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  

  // Handle attendance recording
  const handleAttendanceRecorded = async () => {
    if (selectedSession) {
      await fetchAttendanceData(selectedSession.id);
    }
  };

  // Handle automatic attendance update
  const handleAutoUpdateAttendance = async (session) => {
    try {
      const response = await axios.post(
        `${API_ENDPOINTS.CONFERENCE_ATTENDANCE}/auto-update-pending`,
        { sessionId: session.id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      const { updatedCount, totalRequired, alreadyRecorded, errors } = response.data;
      
      let message = `Successfully updated ${updatedCount} attendance records!\n`;
      message += `• ${updatedCount} users marked as not attended\n`;
      message += `• ${alreadyRecorded} users already had attendance recorded\n`;
      message += `• ${totalRequired} total required attendees`;
      
      if (errors && errors.length > 0) {
        message += `\n• ${errors.length} errors occurred`;
      }
      
      setExportMessage(message);
      setTimeout(() => setExportMessage(""), 8000);
      
      // Refresh attendance data
      await fetchAttendanceData(session.id);
      
    } catch (error) {
      console.error("Error auto-updating attendance:", error);
      const errorMessage = error.response?.data?.error || "Failed to auto-update attendance";
      setExportError(errorMessage);
      setTimeout(() => setExportError(""), 5000);
    }
  };

  // Helper function to check if auto-update button should be shown
  const shouldShowAutoUpdateButton = (sessionDate, endTime) => {
    if (!sessionDate || !endTime) return false;
    
    try {
      // Create session end datetime
      const sessionEndDateTime = new Date(`${sessionDate}T${endTime}`);
      const now = new Date();
      
      // Calculate one hour after session ends
      const oneHourAfterEnd = new Date(sessionEndDateTime.getTime() + (60 * 60 * 1000));
      
      // Show button if current time is more than 1 hour after session ends
      return now > oneHourAfterEnd;
    } catch (error) {
      console.error('Error checking auto-update button visibility:', error);
      return false;
    }
  };

  // Get attendance status badge color
  const getAttendanceStatusBadge = (status) => {
    const colors = {
      'attended': 'success',
      'not_attended': 'danger',
      'pending': 'warning'
    };
    return colors[status] || 'secondary';
  };

  // Render conference type display
  const renderConferenceType = (type) => {
    const resolved = resolveConferenceTypeDisplay(type, conferenceTypeOptions);
    return {
      label: resolved.label,
      color: resolved.color
    };
  };

  // Render conference type as JSX element
  const renderConferenceTypeJSX = (type) => {
    const typeInfo = renderConferenceType(type);
    return (
      <span className="badge align-self-start rounded-2 mt-3" style={toTransparentBadgeStyle(typeInfo.color)}>
        {typeInfo.label}
      </span>
    );
  };

  // Render month view
  const renderMonthView = () => {
    console.log('renderMonthView called with:', {
      currentMonth,
      conferences: Object.values(conferencesByMonth).flat().length,
      selectedAcademicYear,
      availableMonths,
      error,
      loading
    });
    
    if (!currentMonth || Object.values(conferencesByMonth).flat().length === 0) {
      console.log('No current month set or no conferences');
      console.log('Debug info:', {
        currentMonth,
        conferencesLength: Object.values(conferencesByMonth).flat().length,
        selectedAcademicYear,
        availableMonthsLength: availableMonths.length,
        availableAcademicYearsLength: availableAcademicYears.length
      });
      
      return (
        <Card className="mb-4 shadow-sm border-0 py-4">
          <CardBody className="p-0">
            <div className="text-center py-4">
              <div className="d-flex flex-column align-items-center gap-2">
                <img src="/images/undraw_schedule_6t8k.svg" alt="No conferences" className="img-fluid" style={{height: "200px"}} />
                <h5>No Conferences Available</h5>
                <p className="text-muted">
                  {Object.values(conferencesByMonth).flat().length === 0 
                    ? "No conferences have been scheduled yet." 
                    : "No conferences are available for the current academic year."
                  }
                </p>
                <p className="text-muted small">
                  Debug: {Object.values(conferencesByMonth).flat().length} conferences, {availableAcademicYears.length} years, {availableMonths.length} months
                </p>
                <Button 
                  variant="primary" 
                  onClick={() => setShowSchedulingModal(true)}
                  className="me-2"
                >
                  Schedule Your First Conference
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => fetchInitialConferences()}
                  size="sm"
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh Data
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      );
    }
    
    const monthConfs = conferencesByMonth[currentMonth] || [];
    console.log('Month conferences for', currentMonth, ':', monthConfs.length);
    
    const dates = getDatesInMonth(currentMonth);
    const [year, month] = currentMonth.split("-");
    const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    // Get all scheduled dates for this month
    const scheduledDates = dates.filter(date => {
      const conferencesForDate = getConferencesForDate(date, monthConfs);
      return conferencesForDate.length > 0;
    });
    
    console.log('Scheduled dates for', currentMonth, ':', scheduledDates.length);
    
    // Handle date navigation for mobile (account for All tab)
    const handleDateNavigation = (direction) => {
      const totalTabs = 1 + scheduledDates.length; // "All dates" + individual dates
      if (direction === "next" && currentDateIndex < totalTabs - 4) {
        setCurrentDateIndex(currentDateIndex + 1);
      } else if (direction === "prev" && currentDateIndex > 0) {
        setCurrentDateIndex(currentDateIndex - 1);
      }
    };

    // Get visible tabs for mobile (max 4 total: All + up to 3 dates)
    const getVisibleTabs = () => {
      if (!isMobile) {
        return { showAllDates: true, visibleDates: scheduledDates };
      }
      const totalTabs = 1 + scheduledDates.length;
      if (totalTabs <= 4) {
        return { showAllDates: true, visibleDates: scheduledDates };
      }
      const maxVisible = 4;
      const startIndex = Math.max(0, currentDateIndex);
      const endIndex = Math.min(startIndex + maxVisible, totalTabs);
      const showAllDatesLocal = startIndex === 0;
      const visibleDates = scheduledDates.slice(
        Math.max(0, startIndex - 1),
        Math.min(scheduledDates.length, endIndex - 1)
      );
      return { showAllDates: showAllDatesLocal, visibleDates };
    };
    
    if (scheduledDates.length === 0) {
      return (
        <Card className="mb-4 shadow-sm border-0 py-4">
          <CardBody className="p-0">
            <div className="text-center py-4">
              <div className="d-flex flex-column align-items-center gap-2">
                <img src="/images/undraw_schedule_6t8k.svg" alt="No conferences" className="img-fluid" style={{height: "200px"}} />
                <h5>No Conferences Scheduled for This Month</h5>
                <p className="text-muted">No conferences are scheduled for {monthName} in the selected academic year.</p>
                <p className="text-muted">Available months: {availableMonths.join(', ')}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      );
    }
    
    // Use selected from top-level state
    const activeDate = selectedDate || selected;
    const selectedConferences = getConferencesForDate(activeDate, monthConfs);

    // Accordion renderer
    const renderConferenceAccordion = (confs, keyId, forceSingleOpen = true) => {
      const currentKey = accordionOpenKeys[keyId] ?? "0";
      const handleSelect = (eventKey) => {
        setAccordionOpenKeys(prev => {
          const existing = prev[keyId] ?? "0";
          if (!forceSingleOpen) return prev;
          if (eventKey === null || eventKey === existing) return prev;
          return { ...prev, [keyId]: eventKey };
        });
      };
      const accordionProps = forceSingleOpen ? { activeKey: currentKey, onSelect: handleSelect } : { alwaysOpen: true };
      return (
        <Accordion {...accordionProps} className="custom-accordion border-top">
          {confs.map((conf, idx) => {
            const isCancelled = conf.session.isCancelled;
            const timeLabel = conf.startTime && conf.endTime
              ? `${formatTime(conf.startTime)} - ${formatTime(conf.endTime)}`
              : conf.startTime ? `${formatTime(conf.startTime)}` : 'Time not specified';
            const typeInfo = renderConferenceType(conf.type);

            // Resident self-attendance state
            const requiredAttendees = (conf.requiredAttendees || []).map(id => Number(id));
            const currentUserIdForSession = Number(
              JSON.parse(localStorage.getItem('user'))?.id ||
              localStorage.getItem('userId')
            );
            const canRecordSelf = !isCancelled && !!currentUserIdForSession && requiredAttendees.includes(currentUserIdForSession);
            const recordDisabledSelf = shouldDisableRecordAttendance(conf.session.date, conf.endTime);
            const recordedAttendanceForSession = attendanceData[conf.session.id]?.attendance || conf.session.attendance || [];
            const hasRecordedSelf = !!currentUserIdForSession && recordedAttendanceForSession.some(
              r => r.user && r.user.id === currentUserIdForSession && r.status === 'attended'
            );
            const hasNotAttended = !!currentUserIdForSession && recordedAttendanceForSession.some(
              r => r.user && r.user.id === currentUserIdForSession && r.status === 'not_attended'
            );

            return (
              <Accordion.Item eventKey={String(idx)} key={idx} className="border-0 border-bottom">
                <Accordion.Header>
                  <div className="w-100">
                    <div className="d-flex flex-row align-items-center justify-content-between">
                      <div className={` d-flex flex-row align-items-center justify-content-between gap-3 ${isCancelled ? 'text-muted text-decoration-line-through' : ''}`} style={isCancelled ? {opacity: 0.7} : {}}>
                        <div className="d-flex flex-md-row align-items-md-center align-items-start flex-column justify-content-start">
                          <span className="fw-semibold me-5 text-muted">{timeLabel}</span>
                          <div>
                            <span className="fw-semibold me-2">{conf.title}</span>
                            <span className="badge" style={toTransparentBadgeStyle(typeInfo.color)}>{typeInfo.label}</span>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-end me-3 gap-2">
                        {(conf.session.location || conf.location) && (
                          <><MapPin /> <span className="text-muted">{conf.session.location || conf.location}</span></>
                        )}
                        {(conf.zoomLink || conf.session.zoomLink) && (
                          <a 
                            href={conf.zoomLink || conf.session.zoomLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-muted me-2 text-decoration-none cust d-none d-md-block"
                            title="Join Zoom Meeting"
                          >
                            <Video /> <span className="text-muted ms-2">Zoom</span>
                          </a>
                        )}
                        {!isCancelled && isDirector && (
                          <>
                            <Button 
                              size="sm"
                              variant="light"
                              className="d-flex align-items-center justify-content-center gap-2"
                              onClick={(e) => { e.stopPropagation(); setSelectedSession({ ...conf.session, conference: conf }); setShowDetailsForm(true); }}
                            >
                              <SquarePen />
                              <span className="d-none d-md-inline">{(conf.session.talkTitle || conf.session.speaker || conf.session.location || conf.location || conf.zoomLink || conf.session.zoomLink || conf.session.description || (conf.session.attachments && conf.session.attachments.length > 0)) ? 'Edit Details' : 'Add Details'}</span>
                            </Button>
                            <div onClick={(e) => e.stopPropagation()}>
                              <Dropdown align="end">
                                <Dropdown.Toggle variant="outline-danger" size="sm" className="no-arrow">
                                  <i className="bi bi-three-dots-vertical"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => {
                                    if (window.confirm('Are you sure you want to cancel this session?')) {
                                      handleDeleteClick('single', conf);
                                    }
                                  }}> <CircleOff /> Cancel session</Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item onClick={() => {
                                    if (window.confirm('Are you sure you want to permanently delete this session? This cannot be undone.')) {
                                      handleHardDeleteSingleConference(conf);
                                    }
                                  }}> <Trash2 /> Delete session</Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </>
                        )}
                        {isCancelled && (
                          <Badge bg="danger">Cancelled</Badge>
                        )}
                        {/* Resident self-attendance status (desktop) */}
                        {!isCancelled && isResident && !isDirector && canRecordSelf && (
                          hasRecordedSelf ? (
                            <Badge bg="success" className="d-none rounded-pill d-md-flex align-items-center gap-1">
                              <i className="bi bi-check-circle-fill"></i>
                              <span>Attendance Recorded</span>
                            </Badge>
                          ) : hasNotAttended ? (
                            <Badge bg="danger" className="d-none rounded-pill d-md-flex align-items-center gap-1">
                              <i className="bi bi-x-circle-fill"></i>
                              <span>Not Attended</span>
                            </Badge>
                          ) : (
                            <Button 
                              size="sm"
                              variant={recordDisabledSelf ? 'light-solid' : 'primary'}
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setSelectedSession({ ...conf.session, conference: conf }); 
                                setShowAttendanceTracker(true); 
                              }}
                              disabled={recordDisabledSelf}
                              title={recordDisabledSelf ? "Attendance recording is disabled 1 hour after the session ends" : "Record your attendance for this conference"}
                              className="d-none d-md-block"
                            >
                              <i className="bi bi-clipboard-check"></i>
                              <span className="d-none d-md-inline ms-2">{recordDisabledSelf ? "Attendance Closed" : "Record Attendance"}</span>
                            </Button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="px-4 pt-0 pb-4">
                  {!isCancelled ? (
                    <>
                      <Row className="g-2">
                        { (conf.session.image || conf.image) && (
                          <Col lg={3} md={3} sm={12}>
                            {(() => {
                              const raw = conf.session.image || conf.image;
                              const token = localStorage.getItem('token');
                              const url = raw?.startsWith('/uploads/') ? `${window.location.origin}${raw}?token=${token}` : raw;
                              return (
                                <img src={url} alt="Session" className="img-fluid rounded" />
                              );
                            })()}
                          </Col>
                        )}
                        <Col  lg={(conf.session.image || conf.image) ? 6 : 9} md={(conf.session.image || conf.image) ? 9 : 8} className={`${conf.session.image || conf.image ? 'ps-md-3' : ''}`}>
                          <Row>
                            <Col md={7} sm={6} xs={12} className="mb-3">
                              <small className="text-muted fw-semibold">Talk Title</small>
                              <div className="fw-semibold text-body">{conf.session.talkTitle || <span className="text-muted">N/A</span>}</div>
                            </Col>         
                            <Col md={5} sm={6} xs={12} className="mb-3">
                              <small className="text-muted fw-semibold">Talk Time</small>
                              <div className="fw-semibold text-body">{conf.session.talkTime || <span className="text-muted">N/A</span>}</div>
                            </Col>
                            <Col md={7} sm={6} xs={12} className="mb-3">
                              <small className="text-muted fw-semibold">Speaker</small>
                              <div className="fw-semibold text-body">
                                {conf.session.speaker || <span className="text-muted">N/A</span>}
                                {conf.session.speakerTitle && <span className="text-muted"> ({conf.session.speakerTitle})</span>}
                              </div>
                            </Col>
                            {(conf.zoomLink || conf.session.zoomLink) && (
                              <Col md={5} sm={6} xs={12} className="mb-3" >
                                <small className="text-muted fw-semibold">Zoom Link</small>
                                <div className="text-body">
                                  <Button  
                                    variant="primary"
                                    size="sm"
                                    className="fw-semibold"
                                    onClick={() => window.open(conf.zoomLink || conf.session.zoomLink, '_blank')}
                                  >
                                    <i className="bi bi-camera-video me-2"></i>
                                    Join Zoom Meeting
                                  </Button>
                                </div>
                              </Col>
                            )}
                   
                            {conf.session.attachments && conf.session.attachments.length > 0 && (
                          <Col xs={12}>
                            <small className="text-muted fw-semibold">Attachments</small>
                            <div className="list-group mt-2">
                              {conf.session.attachments.map((att, i) => {
                                const getNameFromPath = (p) => {
                                  try { return (p || '').split('/').pop() || `file-${i+1}`; } catch { return `file-${i+1}`; }
                                };
                                const resolvePath = (a) => {
                                  if (typeof a === 'string') return a;
                                  if (a.url) return a.url;
                                  if (a.path) return a.path;
                                  return '';
                                };
                                const attachmentPath = resolvePath(att);
                                const fileName = getNameFromPath(attachmentPath);
                                const fileSize = att.size ? att.size : att.fileSize;
                                const mimeType = att.mimeType || '';
                                const prettySize = (() => {
                                  const bytes = Number(fileSize);
                                  if (!bytes || isNaN(bytes)) return '';
                                  const kb = bytes / 1024;
                                  if (kb < 1024) return `${kb.toFixed(1)} KB`;
                                  const mb = kb / 1024;
                                  return `${mb.toFixed(2)} MB`;
                                })();
                                const handleDownloadAttachment = async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const url = attachmentPath.startsWith('http') ? attachmentPath : 
                                      attachmentPath.startsWith('/uploads/') ? `${API_BASE_URL}${attachmentPath}` : 
                                      `${API_BASE_URL}/uploads/${attachmentPath}`;
                                    const response = await fetch(url, {
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/octet-stream'
                                      }
                                    });
                                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                                    const blob = await response.blob();
                                    const downloadUrl = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = downloadUrl;
                                    link.download = fileName;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(downloadUrl);
                                  } catch (error) {
                                    console.error('Error downloading attachment:', error);
                                    alert('Failed to download attachment. Please try again.');
                                  }
                                };
                                return (
                                  <div key={i} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                    <div className="d-flex align-items-center gap-2">
                                      <i className={`${getFileIcon(fileName, mimeType)} fs-6 text-muted`}></i>
                                      <span className="small">{fileName}</span>
                                      {prettySize && <small className="text-muted">({prettySize})</small>}
                                    </div>
                                    <Button 
                                      size="sm" 
                                      variant="outline-primary" 
                                      onClick={handleDownloadAttachment}
                                    >
                                      <i className="bi bi-download"></i>
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          </Col>
                        )}
                          
                        
                          </Row>
                        </Col>
                        {isDirector && (
                          <Col lg={(conf.session.image || conf.image) ? 3 : 3} md={(conf.session.image || conf.image) ? 12 : 4} sm={12} xs={12} className="mb-3">
                            <div className="bg-light rounded p-2">
                              <div className="mb-2">
                                <span className="small text-muted fw-semibold">Attendance Code</span>
                              </div>
                              
                              <div className="d-flex align-items-center flex-column justify-content-center position-relative rounded-3 mb-2 w-100"
                                style={{
                                  background:
                                    "linear-gradient(45deg, var(--blue) 0%, var(--green) 70%, var(--cyan) 100%)",
                                }}>
                                <code className="d-flex align-items-center h1 fw-bold text-white py-2 m-0" style={{ backgroundColor: 'transparent' }}>
                                  {conf.session.attendanceCode}
                                </code>
                                <div className="w-100 px-0 rounded-bottom-2 position-absolute bottom-0 left-0 right-0 d-flex align-items-center justify-content-center"
                                  style={{
                                    background: "linear-gradient(45deg, transparent 0%, var(--card-bg) 100%)",
                                    height: "10px",
                                  }}
                                ></div>
                              </div>
                              
                              <div className="d-flex flex-row gap-2">
                                <Button 
                                  variant="light" 
                                  size="sm"
                                  onClick={() => { setSelectedSession({ ...conf.session, conference: conf }); setShowQRCodeModal(true); }}
                                  className="w-50 d-flex align-items-center justify-content-center gap-2"
                                >
                                  
                                  <QrCode />
                                  QR Code
                                </Button>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => { setSelectedSession({ ...conf.session, conference: conf }); setShowAttendanceTracker(true); }}
                                  className="w-50 d-flex align-items-center justify-content-center gap-2"
                                >
                                  <ClipboardCheck />
                                  Record
                                </Button>
                              </div>
                            </div>
                          </Col>
                        )}

                        {isResident && !isDirector && (
                          <Col lg={(conf.session.image || conf.image) ? 3 : 3} md={(conf.session.image || conf.image) ? 12 : 4} sm={12} xs={12} className="mb-3">
                            <div className="bg-light rounded p-2">
                              <div className="mb-2">
                                <span className="small text-muted fw-semibold">Attendance Code</span>
                              </div>

                              {shouldShowAttendanceCode(conf.session.date, conf.startTime) ? (
                                <div className="p-2 rounded">
                                  <div className="d-flex  align-items-center flex-column justify-content-center position-relative rounded-3 mb-2 w-100"
                                style={{
                                  background:
                                    "linear-gradient(45deg, var(--blue) 0%, var(--green) 70%, var(--cyan) 100%)",
                                }}>
                                  <div className="d-flex flex-row align-items-center justify-content-between w-100 px-2">
                                    <code className="d-flex  align-items-center h1 fw-bold text-white py-2 m-0" style={{ backgroundColor: 'transparent' }}>
                                      {conf.session.attendanceCode || '—'}
                                    </code>
                                    <Button
                                      variant={copiedCode[conf.session.id] ? 'success' : 'light'}
                                      size="sm"
                                      onClick={() => handleCopyAttendanceCode(conf.session.id, conf.session.attendanceCode)}
                                      className="py-1 px-2"
                                      title={copiedCode[conf.session.id] ? 'Copied!' : 'Copy code'}
                                      disabled={!conf.session.attendanceCode}
                                    >
                                      <i className={`bi ${copiedCode[conf.session.id] ? 'bi-check-lg' : 'bi-clipboard'}`}></i>
                                    </Button>
                                  </div>
                                  <div className="w-100 px-0 rounded-bottom-2 position-absolute bottom-0 left-0 right-0 d-flex align-items-center justify-content-center"
                                  style={{
                                    background: "linear-gradient(45deg, transparent 0%, var(--card-bg) 100%)",
                                    height: "10px",
                                  }}
                                ></div>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-white p-2 rounded">
                                  <div className="text-center text-muted py-2">
                                    <i className="bi bi-clock me-2"></i>
                                    Attendance code will show 1 hour before the start of the session.
                                  </div>
                                </div>
                              )}
                            </div>
                          </Col>
                        )}
                        
                      </Row>
                      <Row>
                        <Col xs={12} className="mb-3">
                          <small className="text-muted fw-semibold">Description</small>
                          <div className="text-body fw-normal">
                            {conf.session.description || <span className="text-muted">N/A</span>}
                          </div>
                        </Col>
                      </Row>

                      {isDirector && (
                      <div className="d-flex flex-row align-items-center justify-content-between">
                        <h6 className="mb-2">Attendance Summary</h6>
                        <small className="text-muted mb-2 d-none d-md-block">
                           {
                            (() => {
                              const requiredAttendeesLocal = conf.requiredAttendees || [];
                              const recordedAttendance = attendanceData[conf.session.id]?.attendance || conf.session.attendance || [];
                              const requiredAttendeeIds = new Set(requiredAttendeesLocal);
                              const requiredAttendance = recordedAttendance.filter(r => requiredAttendeeIds.has(r.user.id));
                              const requiredAttended = requiredAttendance.filter(r => r.status === 'attended').length;
                              const requiredNotAttended = requiredAttendance.filter(r => r.status === 'not_attended').length;
                              const requiredPending = requiredAttendeesLocal.length - requiredAttended - requiredNotAttended;
                              const nonRequiredAttendance = recordedAttendance.filter(r => !requiredAttendeeIds.has(r.user.id));
                              const nonRequiredAttended = nonRequiredAttendance.filter(r => r.status === 'attended').length;
                              let summary = `${requiredAttended} required attended, ${requiredNotAttended} required not attended, ${requiredPending} required pending`;
                              if (nonRequiredAttended > 0) summary += `, ${nonRequiredAttended} optional attended`;
                              return summary;
                            })()
                          }
                        </small>
                        <div className="d-flex flex-row gap-2">
                          {shouldShowAutoUpdateButton(conf.session.date, conf.endTime) && (
                            <Button 
                              size="sm" 
                              variant="warning" 
                              onClick={() => handleAutoUpdateAttendance(conf.session)}
                              title="Mark pending users as not attended (1 hour after session ended)"
                              className="d-flex flex-row align-items-center justify-content-center"
                            >
                              <i className="bi bi-clock-history"></i>
                              <span className="d-none d-md-block ms-2">Auto-Update Attendance</span>
                            </Button>
                          )}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              handleShowAttendanceSummary({
                                ...conf.session,
                                requiredAttendees: conf.requiredAttendees,
                                conferenceTitle: conf.title
                              })
                            }
                            className="d-flex flex-row align-items-center justify-content-center"
                          >
                            <UsersRound />
                            <span className="d-none d-md-inline ms-2">View attendance</span>
                          </Button>
                        </div>
                      </div>
                      )}

                      {/* Resident "Your Attendance" card */}
                      {isResident && (() => {
                        const requiredAttendeesLocal = conf.requiredAttendees || [];
                        const recordedAttendance = attendanceData[conf.session.id]?.attendance || conf.session.attendance || [];
                        const attendanceMap = new Map();
                        recordedAttendance.forEach(record => {
                          if (record.user) {
                            attendanceMap.set(record.user.id, record);
                          }
                        });

                        const currentUserIdStr = localStorage.getItem('userId');
                        const currentUser = Object.values(userData).find(user => 
                          user && String(user.id) === String(currentUserIdStr)
                        );

                        if (!currentUser) {
                          return (
                            <div className="d-flex justify-content-center align-items-center p-3">
                              <div className="text-center text-muted">
                                <Spinner
                                  animation="border"
                                  role="status"
                                  size="sm"
                                  className="me-2"
                                />
                                <span>Loading your attendance details...</span>
                              </div>
                            </div>
                          );
                        }

                        const isRequiredToAttend = requiredAttendeesLocal.includes(currentUser.id);
                        if (!isRequiredToAttend) {
                          return (
                            <div className="text-center text-muted p-3">
                              You are not required to attend this conference.
                            </div>
                          );
                        }

                        const attendanceRecord = attendanceMap.get(currentUser.id);
                        const status = attendanceRecord ? attendanceRecord.status : 'pending';
                        const recordedAt = attendanceRecord?.recordedAt;
                        const userInfo = userData[currentUser.id];

                        return (
                          <Card className="mx-0 mt-2 bg-light">
                            <CardBody className="p-3">
                              <Row className="align-items-center">
                                <Col md={3} className="col-6 mb-md-0 mb-2">
                                  <div className="d-flex flex-md-row flex-column gap-2 justify-content-start align-items-md-center align-items-start text-md-center">
                                    <small className="text-muted fw-semibold d-block">Name</small>
                                    {userInfo ? 
                                      `${userInfo.firstName} ${userInfo.lastName}` : 
                                      attendanceRecord?.user ? 
                                        `${attendanceRecord.user.firstName} ${attendanceRecord.user.lastName}` : 
                                        `User ID: ${currentUser.id}`
                                    }
                                  </div>
                                </Col>
                                <Col md={3} className="col-6 mb-md-0 mb-2">
                                  <div className="d-flex flex-md-row flex-column gap-2 justify-content-start align-items-md-center align-items-start text-md-center">
                                    <small className="text-muted fw-semibold d-block">Role</small>
                                    <span className="text-capitalize">
                                      {userInfo?.role || attendanceRecord?.user?.role || 'Unknown'}
                                    </span>
                                  </div>
                                </Col>
                                <Col md={3} className="col-6 mb-md-0 mb-2">
                                  <div className="d-flex flex-md-row flex-column gap-2 justify-content-start align-items-md-center align-items-start text-md-center">
                                    <small className="text-muted fw-semibold d-block">Status</small>
                                    <Badge 
                                      bg={
                                        status === 'attended' ? 'success' : 
                                        status === 'not_attended' ? 'danger' : 
                                        'warning'
                                      }
                                    >
                                      {status === 'attended' ? 'Attended' :
                                       status === 'not_attended' ? 'Not Attended' :
                                       'Pending'}
                                    </Badge>
                                  </div>
                                </Col>
                                <Col md={3} className="col-6 mb-md-0 mb-2">
                                  <div className="d-flex flex-md-row flex-column gap-2 justify-content-start align-items-md-center align-items-start text-md-center">
                                    <small className="text-muted fw-semibold d-block">Recorded At</small>
                                    <span>
                                      {recordedAt ? 
                                        new Date(recordedAt).toLocaleString() : 
                                        <span className="text-muted">Not recorded</span>
                                      }
                                    </span>
                                  </div>
                                </Col>
                              </Row>
                            </CardBody>
                          </Card>
                        );
                      })()}
                    </>
                  ) : (
                    <div className="text-danger fw-semibold mt-4"> <Alert variant="danger" className=" mb-0"> This conference session was cancelled.</Alert></div>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      );
    };

    const renderAccordion = () => {
      if (showAllDates) {
        return (
          <div>
            {scheduledDates.map((dateObj, di) => {
              const confsForDate = getConferencesForDate(dateObj, monthConfs);
              if (!confsForDate.length) return null;
              const dateLabel = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
              const monthShort = dateObj.toLocaleDateString('en-US', { month: 'short' });
              const dayNum = dateObj.getDate();
              const keyId = `all-${dateObj.toISOString().split('T')[0]}`;
              return (
                <div key={di} className="mb-5">
                  <div className="d-flex flex-row align-items-center justify-content-start gap-3 px-md-4 p-3">
                    <div className="rounded-3 d-flex flex-column border">
                      <div className=" d-flex flex-row align-items-center py-0 px-3 justify-content-center rounded-top bg-light ">
                         <span className="text-muted text-uppercase" style={{fontSize: '0.65rem'}}>{monthShort}</span>
                        </div>
                        <div className="d-flex flex-row align-items-center py-1 justify-content-center">
                      <h6 className="mb-0 fw-bold">{dayNum}</h6>
                      </div>
                      
                        
      
                    </div>
                    <h6 className="mb-0 fw-semibold">{dateLabel}</h6>
                  </div>
                  {renderConferenceAccordion(confsForDate, keyId, false)}
                </div>
              );
            })}
          </div>
        );
      }
      const selectedConferencesLocal = getConferencesForDate(activeDate, monthConfs);
      if (!selectedConferencesLocal.length) {
        return (
          <div className="text-center py-4 text-muted">
            <i className="bi bi-calendar-event" style={{ fontSize: '2rem' }}></i>
            <p className="mb-0">Click on a date above to view conference details</p>
          </div>
        );
      }
      return renderConferenceAccordion(selectedConferencesLocal, 'selected', true);
    };

    return (
      <Card className="mb-4 shadow-sm border-0">
        <CardBody className="p-0">
          <div className=" d-flex flex-row align-items-center justify-content-between flex-wrap gap-2 border-bottom px-3">
            {isMobile && (1 + scheduledDates.length) > 4 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleDateNavigation("prev")}
                disabled={currentDateIndex === 0}
                style={{ padding: '2px 6px', fontSize: '0.75rem' }}
              >
                <i className="bi bi-chevron-left"></i>
              </Button>
            )}
            <Nav variant="tabs" className="flex-wrap gap-md-4 gap-2">
              {(() => {
                const { showAllDates: shouldShowAllDates, visibleDates } = getVisibleTabs();
                const active = selectedDate || selected;
                return (
                  <>
                    {shouldShowAllDates && (
                      <Nav.Item>
                        <Nav.Link active={showAllDates && !selectedDate} onClick={() => { setShowAllDates(true); setSelectedDate(null); }}>
                          <div className="d-flex flex-column align-items-center" style={{ lineHeight: 1.4 }}>
                            <div className="fw-semibold">All</div>
                            <div className="text-muted small">dates</div>
                          </div>
                        </Nav.Link>
                      </Nav.Item>
                    )}
                    {visibleDates.map((date, idx) => {
                      const isActive = active && date.toDateString() === active.toDateString() && !showAllDates;
                      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
                      const dayNum = date.toLocaleDateString('en-US', { day: 'numeric' });
                      const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
                      return (
                        <Nav.Item key={idx}>
                          <Nav.Link active={isActive} onClick={() => { setShowAllDates(false); setSelectedDate(date); setSelected(date); }} className="py-2">
                            <div className="d-flex flex-column align-items-center" style={{ lineHeight: 1.4 }}>
                              <div className="fw-semibold">{weekday}</div>
                              <div className="text-muted small">{dayNum} {monthShort}</div>
                            </div>
                          </Nav.Link>
                        </Nav.Item>
                      );
                    })}
                  </>
                );
              })()}
            </Nav>
            {isMobile && (1 + scheduledDates.length) > 4 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleDateNavigation("next")}
                disabled={currentDateIndex >= (1 + scheduledDates.length) - 4}
                style={{ padding: '2px 6px', fontSize: '0.75rem' }}
              >
                <i className="bi bi-chevron-right"></i>
              </Button>
            )}
          </div>
          <div className={`px-md-4 p-3 bg-light ${showAllDates ? 'border-bottom' : ''} d-flex flex-row align-items-center justify-content-start gap-3`}>
            {showAllDates ? (
              <h6 className="mb-0 fw-semibold">All conferences in {monthName}</h6>
            ) : (
              (() => {
                const monthShort = activeDate
                  ? activeDate.toLocaleDateString('en-US', { month: 'short' })
                  : '';
                const dayNum = activeDate ? activeDate.getDate() : '';

                return (
                  <>
                    <div className="rounded-3 d-flex flex-column border">
                      <div className=" d-flex flex-row align-items-center py-0 px-3 justify-content-center rounded-top bg-light ">
                        <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>
                          {monthShort}
                        </span>
                      </div>
                      <div className="d-flex flex-row align-items-center py-1 justify-content-center bg-lighter rounded-bottom" >
                        <h6 className="mb-0 fw-bold">{dayNum}</h6>
                      </div>
                    </div>

                    <h6 className="mb-0 fw-semibold">
                      {activeDate
                        ? activeDate.toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'Select a date'}
                    </h6>
                  </>
                );
              })()
            )}
          </div>
          {renderAccordion()}
        </CardBody>
      </Card>
    );
  };

  // Copy attendance code helper
  const handleCopyAttendanceCode = async (sessionId, code) => {
    try {
      if (!code) return;
      await navigator.clipboard.writeText(code.toString());
      setCopiedCode(prev => ({ ...prev, [sessionId]: true }));
      setTimeout(() => {
        setCopiedCode(prev => ({ ...prev, [sessionId]: false }));
      }, 1500);
    } catch (error) {
      console.error('Failed to copy attendance code:', error);
    }
  };

  // Handle opening attendance summary modal
  const handleShowAttendanceSummary = (session) => {
    setSelectedSessionForSummary(session);
    setShowAttendanceSummaryModal(true);
  };

  // Handle attendance update - refresh attendance data
  const handleAttendanceUpdated = async () => {
    if (selectedSessionForSummary && selectedSessionForSummary.id) {
      await fetchAttendanceData(selectedSessionForSummary.id);
    }
  };

  return (
    <div>
      <PageTitle title="Conference Scheduler" description="Schedule and manage conference sessions" />
      <Navbar />
      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          {isDirector ? (
            <Row>
              <Col
                lg={3}
                md={6}
                sm={6}
                className="d-flex col-12 mb-md-0 mt-md-0 order-sm-1 order-md-1 order-lg-1  align-items-center justify-content-start gap-3"
              >
               <h6 className="mb-0 fw-semibold">
                  Conferences
                </h6>
                <Form.Group className="mb-0 d-flex flex-row gap-2 justify-content-start align-items-center">
                    <Form.Select
                      size="sm"
                      value={selectedAcademicYear}
                      onChange={(e) => handleAcademicYearChange(e.target.value)}
                      style={{ minWidth: '100px' }}
                      disabled={availableAcademicYears.length === 0}
                    >
                      {availableAcademicYears.length > 0 ? (
                        availableAcademicYears.map(year => (
                          <option key={year} value={year}>
                            {year}-{year + 1}
                          </option>
                        ))
                      ) : (
                        <option value="">No years available</option>
                      )}
                    </Form.Select>
                </Form.Group>
              </Col>
                
                
            
              <Col
                lg={6}
                md={12}
                sm={12}
                className="d-flex gap-2 col-12 order-sm-3 order-md-3 order-lg-2 align-items-center justify-content-between"
              >
                <Nav variant="tabs" className="secondary-nav-tabs">
                  <Nav.Item>
                    <Nav.Link
                      active={activeSecondaryTab === 'conferences'}
                      onClick={() => setActiveSecondaryTab('conferences')}
                    >
                      Conferences
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      active={activeSecondaryTab === 'documents'}
                      onClick={() => setActiveSecondaryTab('documents')}
                    >
                      Documents
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                {activeSecondaryTab === 'conferences' && (
                  <>
                {availableMonths.length > 1 && currentMonthIndex >= 0 && (
                  <>
                    <div className="radio-inputs d-none d-md-flex" style={{ maxWidth: '180px' }}>
                      <label className="radio">
                        <input
                          type="radio"
                          name="month-radio"
                          checked={currentMonthIndex === 0}
                          onChange={() => handleMonthNavigation("prev")}
                          disabled={currentMonthIndex === 0}
                        />
                        <span className="name bg-white-hover"> <i className="bi bi-chevron-left"></i></span>
                      </label>
                      <label className="radio">
                        <span className="name">
                      {currentMonth ? (() => {
                          const [y, m] = currentMonth.split("-");
                          return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                        })() : 'No Schedule'}
                      </span>
                      </label>
                      <label className="radio">
                        <input
                          type="radio"
                          name="month-radio"
                          checked={currentMonthIndex === availableMonths.length - 1}
                          onChange={() => handleMonthNavigation("next")}
                          disabled={currentMonthIndex === availableMonths.length - 1}
                        />
                        <span className="name bg-white-hover"><i className="bi bi-chevron-right"></i></span>
                      </label>
                    </div>
                    <div className="d-md-none">
                        <Form.Select
                          size="sm"
                          value={currentMonth}
                          onChange={(e) => {
                            const selectedMonth = e.target.value;
                            const monthIndex = availableMonths.findIndex(month => month === selectedMonth);
                            if (monthIndex !== -1) {
                              setCurrentMonthIndex(monthIndex);
                              setCurrentMonth(selectedMonth);
                              if (!loadedMonths.includes(selectedMonth)) {
                                applyMonthSlice(selectedMonth, selectedAcademicYear, currentYearConferences);
                              }
                            }
                          }}
                          style={{ minWidth: '150px' }}
                        >
                          {availableMonths.map((month, index) => {
                            const [y, m] = month.split("-");
                            const label = new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                            return (
                              <option key={month} value={month}>
                                {label}
                              </option>
                            );
                          })}
                        </Form.Select>
                    </div>
                  </>
                )}
                </>
                )}
                </Col>
                <Col lg={3} md={6} sm={6} className="d-flex align-items-center justify-content-end order-sm-2 order-md-2 order-lg-3">
                {activeSecondaryTab === 'conferences' && (
                  <>
                <div className="radio-inputs d-flex align-items-center" style={{width: 'fit-content', minWidth: '150px'}}>
                  <label className="radio">
                    <input
                      type="radio"
                      name="radio-input"
                      checked={showSchedulingModal}
                      onChange={() => setShowSchedulingModal(true)}
                    />
                    <span className="name bg-white-hover px-2">
                      <Plus /> <span className="d-none d-md-inline ms-1">Conferences</span></span>
                  </label>
                  <div class="dropdown-container">
                  <DropdownButton 
                    variant="light-solid"
                    size="sm"
                    title={
                      <>
                        <Forward />
                      </>
                    }
                    id="print-email-dropdown"
                    align="end"
                  >
                    <Dropdown.Item onClick={handleExportClick} disabled={exportLoading}>
                      {exportLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Exporting...
                        </>
                      ) : (
                        <>
                          <div className="d-flex flex-row align-items-center">
                             <CalendarCheck /> <span className=" ms-2">Export to Calendar</span>
                          </div>
                        </>
                      )}
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handlePrint}>
                      <Printer /> <span className="ms-2">Print Schedule</span>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleEmailModalOpen}>
                      <Mail /> <span className="ms-2">Email Schedule</span>
                    </Dropdown.Item>
                  </DropdownButton>
                  </div>
                  <div className="dropdown-container">
                  <DropdownButton
                    variant="light-solid"
                    size="sm"
                    title={
                      <>
                        <Trash2 />
                      </>
                    }
                    id="bg-nested-dropdown"
                    align="end"
                  >
                    {(() => {
                      const yearConfs = getConferencesForYear(
                        Object.values(conferencesByMonth).flat(),
                        selectedAcademicYear
                      );
                      const uniqueConferences = yearConfs.filter(
                        (conf, index, self) =>
                          index ===
                          self.findIndex(
                            (c) => c.title === conf.title && c.type === conf.type
                          )
                      );

                      return uniqueConferences.length > 0 ? (
                        uniqueConferences.map((conf, idx) => (
                          <Dropdown.Item
                            key={idx}
                            onClick={() => handleDeleteClick("series", conf)}
                            className="text-danger"
                          >
                            <i className="bi bi-calendar-event me-2"></i>
                            {conf.title}
                          </Dropdown.Item>
                        ))
                      ) : (
                        <Dropdown.Item disabled>
                          <i className="bi bi-calendar-x me-2"></i>
                          No conferences available
                        </Dropdown.Item>
                      );
                    })()}
                  </DropdownButton>
                  </div>
                  </div>
                  </>
                  )}
              </Col>
            </Row>
          ) : (
            <Row>
              <Col className=" col-12 col-lg-3 col-md-4 py-md-0 py-1 d-flex align-items-center justify-content-start gap-2">
                  <h6 className="mb-0 fw-semibold">Conference</h6>
                  <Form.Group className="mb-0 d-flex flex-row gap-2 justify-content-start align-items-center">
                      <Form.Select
                        size="sm"
                        value={selectedAcademicYear}
                        onChange={(e) => handleAcademicYearChange(e.target.value)}
                        style={{ maxWidth: '150px' }}
                        disabled={availableAcademicYears.length === 0}
                      >
                        {availableAcademicYears.length > 0 ? (
                          availableAcademicYears.map(year => (
                            <option key={year} value={year}>
                              {year}-{year + 1}
                            </option>
                          ))
                        ) : (
                          <option value="">No years available</option>
                        )}
                      </Form.Select>
                  </Form.Group>
                </Col>
                <Col className=" col-12 col-lg-9 col-md-8 d-flex align-items-center justify-content-between gap-3">
                <Nav variant="tabs" className="secondary-nav-tabs">
                    <Nav.Item>
                      <Nav.Link
                        active={activeSecondaryTab === 'conferences'}
                        onClick={() => setActiveSecondaryTab('conferences')}
                      >
                        Conferences
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        active={activeSecondaryTab === 'documents'}
                        onClick={() => setActiveSecondaryTab('documents')}
                      >
                        Documents
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
               
                 { activeSecondaryTab === 'conferences' && (
                  <>
                    {availableMonths.length > 1 && currentMonthIndex >= 0 && (
                  <>
                    <div className="radio-inputs d-none d-md-flex" style={{ maxWidth: '180px' }}>
                      <label className="radio">
                        <input
                          type="radio"
                          name="month-radio"
                          checked={currentMonthIndex === 0}
                          onChange={() => handleMonthNavigation("prev")}
                          disabled={currentMonthIndex === 0}
                        />
                        <span className="name bg-white-hover"><i className="bi bi-chevron-left"></i></span>
                      </label>
                      <label className="radio">
                        <span className="name">
                      {currentMonth ? (() => {
                          const [y, m] = currentMonth.split("-");
                          return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                        })() : 'No Schedule'}
                      </span>
                      </label>
                      <label className="radio">
                        <input
                          type="radio"
                          name="month-radio"
                          checked={currentMonthIndex === availableMonths.length - 1}
                          onChange={() => handleMonthNavigation("next")}
                          disabled={currentMonthIndex === availableMonths.length - 1}
                        />
                        <span className="name bg-white-hover"><i className="bi bi-chevron-right"></i></span>
                      </label>
                    </div>
                    <div className="d-md-none">
                        <Form.Select
                          size="sm"
                          value={currentMonth}
                          onChange={(e) => {
                            const selectedMonth = e.target.value;
                            const monthIndex = availableMonths.findIndex(month => month === selectedMonth);
                            if (monthIndex !== -1) {
                              setCurrentMonthIndex(monthIndex);
                              setCurrentMonth(selectedMonth);
                              if (!loadedMonths.includes(selectedMonth)) {
                                applyMonthSlice(selectedMonth, selectedAcademicYear, currentYearConferences);
                              }
                            }
                          }}
                          style={{ minWidth: '150px' }}
                        >
                          {availableMonths.map((month, index) => {
                            const [y, m] = month.split("-");
                            const label = new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                            return (
                              <option key={month} value={month}>
                                {label}
                              </option>
                            );
                          })}
                        </Form.Select>
                    </div>
                  </>
                )}
                </>
                )} 
              </Col>
            </Row>
          )}
        </Container>
      </div>

      <Container className="mt-4">
        {/* Move exportMessage/exportError alerts here, above the schedule card */}
        {exportMessage && <div className="alert alert-success mb-3">{exportMessage}</div>}
        {exportError && <div className="alert alert-danger mb-3">{exportError}</div>}
        <Row className="justify-content-center">
          <Col lg={12} md={12} sm={12}>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading conferences...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : (
              activeSecondaryTab === 'conferences' ? renderMonthView() : renderDocumentsView()
            )}
          </Col>
        </Row>

        {/* Conference Scheduling Modal */}
        {isDirector && (
          <ConferenceSchedulingModal
            show={showSchedulingModal}
            onHide={() => setShowSchedulingModal(false)}
            onConferenceCreated={handleConferenceCreated}
            academicYear={selectedAcademicYear}
          />
        )}

        {/* Conference Details Modal */}
        <ConferenceDetailsModal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          date={selectedDate}
          onSessionClick={handleSessionClick}
        />

        {/* Attendance Tracker Modal */}
        <AttendanceTracker
          show={showAttendanceTracker}
          onHide={() => setShowAttendanceTracker(false)}
          session={selectedSession}
          onAttendanceRecorded={handleAttendanceRecorded}
        />
        <ConferenceDetailsForm
          show={showDetailsForm}
          onHide={() => setShowDetailsForm(false)}
          session={selectedSession}
          onDetailsSaved={() => { setShowDetailsForm(false); fetchInitialConferences(); }}
        />
        
        {/* QR Code Display Modal */}
        <QRCodeDisplay
          show={showQRCodeModal}
          onHide={() => setShowQRCodeModal(false)}
          session={selectedSession}
          conference={selectedSession?.conference}
        />
        
        {/* Attendance Summary Modal */}
        <AttendanceSummaryModal
          show={showAttendanceSummaryModal}
          onHide={() => setShowAttendanceSummaryModal(false)}
          session={selectedSessionForSummary}
          attendanceData={attendanceData}
          userData={userData}
          isDirector={isDirector}
          onAttendanceUpdated={handleAttendanceUpdated}
        />
        
        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton className="border-bottom-0">
            <Modal.Title className="d-flex flex-column justify-content-start align-items-start w-100">
              <span>Confirm Deletion</span>
              <div className="text-hl-danger"></div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {deleteType === "current" ? (
              <div>
                <p>Are you sure you want to delete all conferences for the selected date?</p>
                <p className="text-muted">
                  <strong>Date:</strong> {(selectedDate || selected)?.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
                <p className="text-danger">
                  <i className="bi bi-exclamation-circle me-1"></i>
                  This action cannot be undone.
                </p>
              </div>
            ) : deleteType === "all" ? (
              <div>
                <p>Are you sure you want to delete ALL conferences for the current academic year?</p>
                <p className="text-muted">
                  <strong>Academic Year:</strong> {selectedAcademicYear}-{selectedAcademicYear + 1}
                </p>
                <p className="text-danger">
                  <i className="bi bi-exclamation-circle me-1"></i>
                  This will permanently delete all conference schedules and cannot be undone.
                </p>
              </div>
            ) : deleteType === "series" && selectedConference ? (
              <div>
                <p>Are you sure you want to delete the entire conference series?</p>
                <p className="text-muted">
                  <strong>Series:</strong> {selectedConference.title} ({selectedConference.type})
                </p>
                <p className="text-muted">
                  <strong>Academic Year:</strong> {selectedAcademicYear}-{selectedAcademicYear + 1}
                </p>
                <p>
                  
                  This will delete all events in this series and cannot be undone.
                </p>
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer className="border-top-0">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteConferences}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="bi bi-trash me-1"></i>
                  Delete
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Export Confirmation Modal */}
        <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
          <Modal.Header closeButton className="border-bottom-0">
           <div className="d-flex flex-column justify-content-start align-items-start w-100">
             <Modal.Title>Export to Calendar</Modal.Title>
             <div className="text-hl"></div>
             </div>

          </Modal.Header>
          <Modal.Body>
            <p>This will export all future conference sessions for the selected month to individual user calendars.</p>
            
            {(() => {
              const summary = getExportSummary();
              return (
                <div className="bg-light p-3 rounded mb-3">
                  <h6 className="mb-2">Export Summary:</h6>
                  <div className="row">
                    <div className="col-6">
                      <small className="text-muted">Academic Year:</small><br/>
                      <strong>{selectedAcademicYear}-{selectedAcademicYear + 1}</strong>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Month:</small><br/>
                      <strong>{summary.month}</strong>
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-4">
                      <small className="text-muted">Conferences:</small><br/>
                      <strong>{summary.conferences}</strong>
                    </div>
                    <div className="col-4">
                      <small className="text-muted">Sessions:</small><br/>
                      <strong>{summary.sessions}</strong>
                    </div>
                    <div className="col-4">
                      <small className="text-muted">Attendees:</small><br/>
                      <strong>{summary.attendees}</strong>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>What will happen:</strong>
              <ul className="mb-0 mt-2">
                <li>Prior conference events for this month will be deleted (except those that have already passed)</li>
                <li>New events will be created for all future conference sessions in the selected month</li>
                <li>Events will include title, location, time, description, and attachments</li>
                <li>Events will be created for all required attendees</li>
                <li>Past sessions will be skipped automatically</li>
              </ul>
            </div>
            
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Note:</strong> This action cannot be undone. Only directors can perform this operation.
            </div>
          </Modal.Body>
          <Modal.Footer className="border-top-0">
            <Button variant="secondary" size="sm" onClick={() => setShowExportModal(false)} disabled={exportLoading}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              onClick={handleExportConfirm}
              disabled={exportLoading}
            >
              {exportLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload me-1"></i>
                  Export to Calendar
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Email Modal */}
        <Modal show={showEmailModal} onHide={() => {
          setShowEmailModal(false);
          setEmailAddress("");
          setSelectedUserIds([]);
          setUserSearchTerm("");
          setSelectedRole("");
          setSelectedPGY("");
          setShowUserDetails(false);
        }} size="lg">
          <Modal.Header closeButton>
            <div className="d-flex flex-column justify-content-start align-items-start w-100">
              <Modal.Title>Email Conference Schedule</Modal.Title>
              <div className="text-hl"></div>
            </div>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Form>
              {/* Manual Email Input */}
              <Row>
                <Col md={6}>
              <Form.Group className="mb-4">
                <FloatingLabel controlId="floatingInput" label="Additional Email Address (Optional)" className="mb-3">
                <Form.Control
                  type="email"
                  placeholder="Enter email address (optional)"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  disabled={emailLoading}
                />
                <Form.Text className="text-muted">
                  You can add an email address that is not in the user list below.
                </Form.Text>
                </FloatingLabel>
              </Form.Group>
              {/* Selected Users Display */}
              {selectedUserIds.length > 0 && (
                <div className="mb-3">
                  <Form.Label>Selected Users ({selectedUserIds.length}):</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedUserIds.slice(0, 6).map(userId => {
                      const user = users.find(u => u.id === userId);
                      if (!user) return null;
                      return (
                        <span key={userId} className="badge rounded bg-secondary d-flex align-items-center gap-2 p-2" style={{ fontSize: '1em' }}>
                          <span className="fw-normal">{user.firstName} {user.lastName}</span>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger px-1 py-0"
                            onClick={() => handleUserSelection(userId)}
                            title="Remove user"
                            style={{ lineHeight: 1, fontSize: '.875rem'}}
                          >
                            <i className="bi bi-x"></i>
                          </Button>
                        </span>
                      );
                    })}
                  </div>
                  {selectedUserIds.length > 6 && (
                    <div>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-decoration-none px-1 py-0"
                        onClick={() => setShowUserDetails(!showUserDetails)}
                      >
                        <i className={`bi bi-chevron-${showUserDetails ? 'up' : 'down'} me-1`}></i>
                        {showUserDetails ? 'Hide' : `Show ${selectedUserIds.length - 6} more users`}
                      </Button>
                      {showUserDetails && (
                        <div className="mt-2 d-flex flex-wrap gap-2">
                          {selectedUserIds.slice(6).map(userId => {
                            const user = users.find(u => u.id === userId);
                            if (!user) return null;
                            return (
                              <span key={userId} className="badge rounded bg-secondary d-flex align-items-center gap-2 p-2" style={{ fontSize: '1em' }}>
                                <span className="fw-normal">{user.firstName} {user.lastName}</span>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-danger px-1 py-0"
                                  onClick={() => handleUserSelection(userId)}
                                  title="Remove user"
                                  style={{ lineHeight: 1, fontSize: '.875rem'}}
                                >
                                  <i className="bi bi-x"></i>
                                </Button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              </Col>

                <Col md={6} className="bg-light rounded p-2">
              {/* User Selection */}
              <Form.Group className="mb-3">
                
                {/* Search and Filter Row */}
                <div className="row g-2 mb-2">
                  <div className="col-lg-8 col-md-8">
                    <div className="position-relative">
                      <Form.Control
                        type="text"
                        placeholder="Search users..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        disabled={usersLoading}
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-4">
                    <Dropdown show={showFilterDropdown} onToggle={(isOpen) => setShowFilterDropdown(isOpen)} align="end" autoClose="outside">
                      <Dropdown.Toggle id="filter-dropdown-toggle" variant="outline-primary" size="sm" className="mb-2 w-100 no-arrow">
                        <i className="bi bi-funnel"></i> Filter
                      </Dropdown.Toggle>
                      <Dropdown.Menu id="filter-dropdown-menu" className="p-3" style={{ minWidth: 260 }}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1">Role</Form.Label>
                          <Form.Select
                            value={selectedRole}
                            onChange={(e) => {
                              setSelectedRole(e.target.value);
                              if (e.target.value !== 'resident') {
                                setSelectedPGY("");
                              }
                            }}
                          >
                            <option value="">All Roles</option>
                            <option value="resident">Residents</option>
                            <option value="attending">Attendings</option>
                            <option value="director">Directors</option>
                            <option value="medical student">Medical Students</option>
                            <option value="administrative assistant">Administrative Assistants</option>
                          </Form.Select>
                        </Form.Group>
                        {selectedRole === 'resident' && (
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1">PGY Level</Form.Label>
                            <Form.Select
                              value={selectedPGY}
                              onChange={(e) => setSelectedPGY(e.target.value)}
                            >
                              <option value="">All</option>
                              {Array.from({ length: 5 }, (_, i) => i + 1).map(level => (
                                <option key={level} value={level}>
                                  PGY-{level}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        )}
                        <div className="d-flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            className="flex-fill"
                            onClick={() => setShowFilterDropdown(false)}
                            disabled={!selectedRole && !selectedPGY}
                          >
                            Apply Filter
                          </Button>
                          <Button
                            variant="light"
                            size="sm"
                            className="flex-fill"
                            onClick={() => {
                              setUserSearchTerm("");
                              setSelectedRole("");
                              setSelectedPGY("");
                              setShowFilterDropdown(false);
                            }}
                            disabled={!userSearchTerm && !selectedRole && !selectedPGY}
                          >
                            Clear
                          </Button>
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>

                {/* Results Summary */}
                {(userSearchTerm || selectedRole || selectedPGY) && (
                  <div className="mb-2">
                    <small className="text-muted">
                      Showing {getFilteredUsers().length} of {users.length} users
                      {userSearchTerm && ` matching "${userSearchTerm}"`}
                      {selectedRole && ` with role "${selectedRole}"`}
                      {selectedPGY && ` at PGY-${selectedPGY} level`}
                    </small>
                  </div>
                )}

                {/* Users List */}
                <div className="border rounded p-2 bg-white" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {usersLoading ? (
                    <div className="text-center py-3">
                      <Spinner size="sm" />
                      <span className="ms-2">Loading users...</span>
                    </div>
                  ) : getFilteredUsers().length > 0 ? (
                    <>
                      {/* Select All Checkbox */}
                      <div className="form-check border-bottom pb-2 mb-2">
                        <Form.Check
                          type="checkbox"
                          id="select-all-users"
                          checked={getFilteredUsers().length > 0 && getFilteredUsers().every(user => selectedUserIds.includes(user.id))}
                          onChange={() => {
                            const filteredUserIds = getFilteredUsers().map(u => u.id);
                            const allSelected = getFilteredUsers().every(user => selectedUserIds.includes(user.id));
                            
                            if (allSelected) {
                              setSelectedUserIds(prev => prev.filter(id => !filteredUserIds.includes(id)));
                            } else {
                              setSelectedUserIds(prev => {
                                const newSelection = [...prev];
                                filteredUserIds.forEach(id => {
                                  if (!newSelection.includes(id)) {
                                    newSelection.push(id);
                                  }
                                });
                                return newSelection;
                              });
                            }
                          }}
                          label={
                            <span className="fw-semibold">
                              {getFilteredUsers().every(user => selectedUserIds.includes(user.id)) ? 'Deselect All' : 'Select All'}
                              <span className="text-muted ms-2">({getFilteredUsers().length} users)</span>
                            </span>
                          }
                        />
                      </div>
                      
                      {/* Individual User Checkboxes */}
                      {getFilteredUsers().map(user => (
                        <div key={user.id} className="form-check">
                          <Form.Check
                            type="checkbox"
                            id={`user-${user.id}`}
                            checked={selectedUserIds.includes(user.id)}
                            onChange={() => handleUserSelection(user.id)}
                            label={
                              <span>
                                <strong>{user.firstName} {user.lastName}</strong>
                                <span className="text-muted ms-2">({user.role})</span>
                                {user.specialty && <span className="text-info ms-2">{user.specialty}</span>}
                              </span>
                            }
                          />
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-center text-muted py-3">
                      {userSearchTerm || selectedRole ? 'No users found matching your criteria' : 'No users available'}
                    </div>
                  )}
                </div>
                
                <Form.Text className="text-muted">
                  Selected users will receive the conference schedule via email
                </Form.Text>
              </Form.Group>
              </Col>
              </Row>
              
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-top-0">
            <Button 
              variant="secondary" 
              size="sm"
              onClick={() => {
                setShowEmailModal(false);
                setEmailAddress("");
                setSelectedUserIds([]);
                setUserSearchTerm("");
                setSelectedRole("");
                setSelectedPGY("");
                setShowUserDetails(false);
              }}
              disabled={emailLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              onClick={handleSendEmail}
              disabled={emailLoading || (selectedUserIds.length === 0 && !emailAddress.trim())}
            >
              {emailLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending...
                </>
              ) : (
                <>
                  <i className="bi bi-envelope me-2"></i>
                  Send Email {selectedUserIds.length > 0 && `(${selectedUserIds.length + (emailAddress.trim() ? 1 : 0)})`}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default ConferenceSchedulerPage; 